import { r as recognizeNaturalNumbers } from './recognize-natural-numbers.js';
import { t as translateDate } from './translate-date.js';
import './index2.js';

/**
 * **Synxis**
 *
 * Official: www.sabrehospitality.com
 *
 * Example: <a href="https://gc.synxis.com/rez.aspx?hotel=56008&promo=&chain=13633&template=RBEDX&shell=GCF2&start=availresults&locale=en-US&arrive=05%2F31%2F2018&depart=06%2F01%2F2018&adult=1&child=0&room=" target="_blank">JA Jebel Ali Beach Hotel</a>
 *
 * @exports bs_6
 * @category engines
 */
function factory({ window, document, thn }) {
  const engine = {};
  let IS_RESERV_HOLD;
  const isMaiamiCoconutGrove = thn.data.propertyId === 1024383; //  Mr. C Miami Coconut Grove
  const isGrandHotelExcelsior = thn.data.accountUserId === 1041006;
  const isFirstHotels = thn.data.accountUserId === 1044655;
  const isAmanHotels = thn.data.accountUserId === 1042737;
  const isCarnerosResort = thn.data.propertyId === 1024520;
  const isJungleBayDominica = thn.data.propertyId === 1162113;
  const wantsToUseAllRates = [1039600, 1040727, 1032576, 1034092, 1011734, 1013927, 1162237].includes(
    thn.data.propertyId,
  );
  // This filter uses a regex validation that is configured in property.settings as engine_rooms_filter
  const filterForRooms = thn.getSetting('engine_rooms_filter');
  let refreshPriceByAside;
  const nonRefundableRateCode = ['D-FPP1']; // Non refundable rate code for instant booking
  const usersUsingRoomsDispoInstantBooking = [1040508];
  const isOnyxHospitality = thn.data.accountUserId === 1039498;
  const isSeasidePalmBeach = thn.data.propertyId === 1034331;
  // Some clients just want the PW to show the first rate available, and not refresh when the user applies filters
  const usersOnlyWantToShowFirstRate = isSeasidePalmBeach;

  /**
   * Clarity is a Microsoft website monitoring tool that affects us due to its architecture.
   * Clarity is hijacking pushstate events, causing us to not detect page changes.
   * This method is a defensive code that, if Clarity exists, allows us to stop it while our script works.
   */
  engine._clarityGuard = function _clarityGuard() {
    const clarityStopHandler = function () {
      if (typeof window.clarity === 'function') {
        window.clarity('stop');
      }
    };
    const clarityStartHandler = function () {
      if (typeof window.clarity === 'function') {
        window.clarity('start');
      }
    };
    thn.bus.on('thn:listen#start', clarityStopHandler);
    thn.bus.on('thn:listen#end', clarityStartHandler);
    thn.shutdown(function () {
      thn.bus.detach('thn:listen#start', clarityStopHandler);
      thn.bus.detach('thn:listen#end', clarityStartHandler);
    });
  };

  engine._isBarcelo = function _isBarcelo() {
    return thn.data.accountUserId === 1032989;
  };

  /**
   * At the moment only the Avia Hotels chain is complaining that we are registering
   * reservations made through the call center, when this happens their agents put
   * the reservation on hold until the customer pays.
   */
  engine._checkReservationHold = function _checkReservationHold() {
    IS_RESERV_HOLD = !!(thn.objectGet(window.dataLayer, 'ConfirmNo')
      && thn.objectGet(window.dataLayer, 'DetailedResvStatus')) === 'OnHold';
  };

  engine.getInstantBookingMountSelector = function getInstantBookingMountSelector() {
    return '.thumb-cards_button';
  };

  engine.parseInstantBookingRateDetails = function parseInstantBookingRateDetails(element) {
    const container = element.closest('.thumb-cards_rate');

    if (!container) {
      return null;
    }

    // Data we already obtain with priceParseData
    const searchDataRaw = engine.priceParseData();

    if (!searchDataRaw || !('price' in searchDataRaw)) {
      return null;
    }

    const perks = undefined;
    const rateId = container.getAttribute('data-rate-code') || undefined;
    const rateName = container.querySelector('.app_subheading2')?.textContent || undefined;
    const roomId = container.closest('[data-room-code]')?.getAttribute('data-room-code') || undefined;
    const roomName = container.closest('.thumb-cards_container')?.querySelector('.thumb-cards_cardHeader .app_heading1')?.textContent || undefined;
    const roomsDescription = container.closest('.thumb-cards_container')?.querySelector('.thumb-cards_cardHeader .thumb-cards_roomShortDesc')?.textContent || undefined;
    const roomPicture = container.closest('.thumb-cards_container')?.querySelector('.thumb-cards_imgWrapper img')?.getAttribute('src') || undefined;
    const isRefundable = !nonRefundableRateCode.includes(rateId);
    const price = thn.parseNumber(container.querySelector('.thumb-cards_price')?.textContent);
    const rateDetails = {
      rateId,
      rateName,
      roomId,
      roomName,
      roomsDescription,
      roomPicture,
      isRefundable,
      perks,
      currency: searchDataRaw.currency || '',
      dateBegin: searchDataRaw.dateBegin || '',
      dateEnd: searchDataRaw.dateEnd || '',
      adults: searchDataRaw.adults,
      children: searchDataRaw.children,
      roomsDispo: searchDataRaw.roomsDispo,
      price,
    };

    return rateDetails;
  };

  /**
   * @memberOf module:bs_6
   * @param ctx
   */
  engine.onHashChange = function onHashChange(ctx) {
    if (thn.data.propertyId === 1027865) {
      thn.pageCheck();
    }
  };

  /**
   * @memberOf module:bs_6
   * @see {@link boot}
   *
   * @param {object} ctx - The context object.
   */
  engine.boot = function boot() {
    // Run function to protect ourselves from Clarity
    engine._clarityGuard();

    const languageChecker = () => {
      if (engine._isReact()) {
        engine._afterLoading(thn.languageCheck);
      } else {
        thn.languageCheck();
      }
    };

    thn.bus.on('dataLayer:event.language', languageChecker, true);
    thn.shutdown(() => {
      thn.bus.detach('dataLayer:event.language', languageChecker);
    });

    // Hotel Estelar
    if (thn.data.accountUserId === 1033828 && window.location.hostname.match(/hotelesestelar/)) {
      thn.shutdown(thn.delegate('click', 'a.nturl', thn.languageCheck),
      );
    }

    // Hoteles Click-Clack
    if (
      (thn.data.accountUserId === 1036777)
      && window.location.hostname.match(/clickclackhotel/)
    ) {
      thn.shutdown(
        thn.delegate(
          'click',
          'a[title="Español"], a[title="English"]',
          thn.languageCheck,
        ),
      );
    }

    // For Barceló we push their "data object" into the standard dataLayer,
    // for later usage in the targeting rules
    if (engine._isBarcelo()) {
      const dataLayer = window.dataLayer;
      // eslint-disable-next-line camelcase
      const utag_data = window.utag_data;
      // eslint-disable-next-line camelcase
      if (dataLayer && utag_data) {
        dataLayer.push({
          event: 'thn.engine.boot',
          utag_data,
        });
      }
    }

    // For Baros Hotel (pid:1027117) add custom scroll listener for corporate desktop site
    if (
      thn.data.propertyId === 1027117 && window.baros_smooth_scrollbar
      && !!window.baros_smooth_scrollbar.customScroll
    ) {
      const scrollListener = () => {
        thn.bus.emit('window:scroll', null, {
          target: window.document,
        });
      };

      window.baros_smooth_scrollbar.customScroll.addListener(scrollListener);
      thn.shutdown(function () {
        window.baros_smooth_scrollbar.customScroll.removeListener(scrollListener);
      });
    }

    // Observer to detect applied filter
    const filterContainer = document.querySelector('.filter-bar_box');
    if (filterContainer && !isAmanHotels && !isCarnerosResort) {
      const loader = document.querySelector('.spinner_main');
      const refreshPage = thn.debounce(() => {
        if (thn.data.pageName === 'Rooms and Rates') {
          if (!loader) {
            thn.widgets.price.refresh();
          }
        }
      }, 800);

      thn.observeDom(filterContainer, refreshPage, {
        childList: true,
      });

      thn.shutdown(() => {
        refreshPage.cancel();
        thn.stopObserveDom(filterContainer, refreshPage);
      });
    }

    const pricesContainer = document.querySelector('#mainContent');
    if (pricesContainer) {
      const refreshPage = thn.debounce(thn.widgets.price.update(), 700);
      thn.observeDom(pricesContainer, refreshPage, {
        childList: true,
        subtree: true,
        attributes: false,
      });

      thn.shutdown(() => {
        refreshPage.cancel();
        thn.stopObserveDom(pricesContainer, refreshPage);
      });
    }

    if (isAmanHotels) {
      const container = document.body;
      if (container && window.location.hostname === 'www.aman.com') { // Calendar page check
        const pageCheckCalendar = thn.debounce(thn.pageCheck, 400);
        thn.observeDom(container, pageCheckCalendar, {
          childList: false,
          subtree: false,
          attributes: true,
        });

        thn.shutdown(() => {
          pageCheckCalendar.cancel();
          thn.stopObserveDom(container, pageCheckCalendar);
        });
      }

      const undelegate = thn.delegate(
        'click',
        '.filter-bar_matchingRooms',
        function () {
          thn.widgets.price.refresh();
        },
      );
      thn.shutdown(undelegate);
    }

    // Detect if Calendar is visible in HP
    const isCalendarVisibleHP = document.querySelector('.sabre-widget--calendar');
    if (isCalendarVisibleHP) {
      const pageCheckCalendar = thn.debounce(() => {
        thn.pageCheck();
      }, 400);

      thn.observeDom(isCalendarVisibleHP, pageCheckCalendar, {
        childList: true,
        subtree: true,
        attributes: true,
      });

      thn.shutdown(() => {
        pageCheckCalendar.cancel();
        thn.stopObserveDom(isCalendarVisibleHP, pageCheckCalendar);
      });
    }

    const searchBarContainer = document.querySelector(
      '.search-bar-container_inner, .search-bar-mobile-container_container, #mainContent',
    );
    if (searchBarContainer && thn.data.pageName !== 'Calendar') {
      const refreshPage = thn.debounce(() => {
        const loader = document.querySelector(
          '.spinner_container.spinner_centered',
        );
        if (!loader) {
          thn.pageCheck();
        }

      }, 800);
      thn.observeDom(searchBarContainer, refreshPage, {
        childList: true,
        subtree: true,
        attributes: false,
      });

      thn.shutdown(() => {
        refreshPage.cancel();
        thn.stopObserveDom(searchBarContainer, refreshPage);
      });
    }

    // Observer to detect the calendar and refresh the page when it is opened or closed
    const containerHeader = document.querySelector('#mainContent header');
    if (containerHeader) {
      const refreshPageHeader = thn.debounce(thn.pageCheck, 400);
      thn.observeDom(containerHeader, refreshPageHeader, {
        childList: true,
        subtree: true,
        attributes: false,
      });

      thn.shutdown(() => {
        refreshPageHeader.cancel();
        thn.stopObserveDom(containerHeader, refreshPageHeader);
      });
    }

    // Detect if Calendar is visible in HP for Woodlark Hotel
    if (
      thn.data.accountUserId === 1043778
      && window.location.hostname === 'www.woodlarkhotel.com'
    ) {
      const calendarContainer = document.querySelector('.booking-popup');
      if (calendarContainer) {
        const refreshPageWood = thn.debounce(thn.pageCheck, 400);
        thn.observeDom(calendarContainer, refreshPageWood, {
          attributes: true,
        });

        thn.shutdown(() => {
          refreshPageWood.cancel();
          thn.stopObserveDom(calendarContainer, refreshPageWood);
        });
      }
    }
  };

  engine._getRatesBlacklistRegexp = function () {
    // Add property/user specific rates to exclude
    let extraBlacklistRates = '';
    const preferredHotels = [
      1032305,
      1032644,
      1032453,
      1032158,
      1032301,
      1032304,
    ];
    const excludeRatesHotels = { // There are specific configurations for each hotel of the preferred chain and other properties.
      1032387:
        `S3AP|S3A|SAPP|SAP|GFED00|EGPD00|GFEDPD|LSNR00|MKTLOV|PAGPL1|PAGPL2|PAGPL3|PBKPL1|PBKPL2|PBKPL3|PEXHL1|PEXHL2
                        |PEXHL3|PEXPL1|PEXPL2|PEXPL3|PLMPL3|PTWPL1|PTWPL2|PTWPL3|SOGPL1|SOGPL2|SOGPL3|LJUL25|SSR|IPPOINTS|IPRNP`,
      1032444:
        'DISAAA|DISARP|FEDGOV|STATEGOV|TAXEGOVT|MKTLOV|MILITARY|IPPOINTS|IPRNP',
      1032173:
        'AAARP|DISAAA|AAARP|DISAARP|DISARP|GOVGOV|AMAZON|NEIGHBOR|RESIDENT|IPPOINTS|IPRNP',
      1032660:
        'CBGOVGUINEA|CBGOVCOREE|CBGOVMAF|CBGOVMAFBB|GCBCCI|GCBCORPCAMPUS|GCBCORPKPMG|GCBCORPUEM|GCBSAM|IPPOINTS|IPRNP',
      1032232:
        'DEVILSS|PKG06R|PKG06RRS|PKG3000|PKGAE|PKGAE3600|PKGLegendary|PKGLobs|PKGYOLO|IPPOINTS|IPRNP',
      1154011:
        'DISAAA|DISAARP|NEGBCGEU|HOLHANG|MKTLOV|DISSEN|SENIOR|IPPOINTS|IPRNP',
      1033367:
        'MKTLOV|VH21ADLE|VH21LEGA|VH21NLRA|VH21SANT|VH22LRA|VHNEWLRA|IPPOINTS|IPRNP',
      1032664: 'DISAAAARP|DISAAA|DISAARP|DISCMIL|DISAARP|IPPOINTS|IPRNP',
      1037083: 'AAA|AARP',
      1037235: 'ROMANCE|CAAARP',
      1038666: 'G-GOVF|D-AAAR|D-SENR|CN-JESSENIA|GOVERNMENT',
      1029991: 'DIAMOND|GOLD|SILVER',
      1038802: 'AAA|AARP|MILT',
      1039400: 'MILITARY|AAAA',
      1045750: 'MILITARY|D-AAAR|D-SENR',
      1035173: 'GOV',
      1041382: 'AARP',
    };

    if (![1036319, 1044040, 1040848].includes(thn.data.accountUserId)) { // Coco collection && BoTree
      extraBlacklistRates += '|MKTADV';
    }
    if (thn.data.propertyId === 1025778) {
      extraBlacklistRates += '|MEMBER|MOMAP|APMO';
    }
    if (thn.data.propertyId === 1014048) {
      extraBlacklistRates += '|SUMMER|BREAKFAST';
    }

    if (thn.data.propertyId === 1042713) {
      extraBlacklistRates += '|ASSN|COMP|CORPNEG|DIS|EDU|FIT|GCORP|GOV|INHS|OTA|PKG|PROMO';
    }
    // As these are really short rates, should be separated for now
    if (thn.data.accountUserId === 1034101) { // Pacifica Hotels
      extraBlacklistRates += '|3A|SR|GV';
    }
    if (preferredHotels.includes(thn.data.propertyId)) {
      extraBlacklistRates
        += `|GOVGOV|NEGGOC1|NEGOVA|NEGGOC|DISAAA1|DISAAA|DISARP|DISCMIL|DISAAAARP|DISAARP|FL/GA Resident|PROFLR|GOVTO|NR-GTOUR|NR-GGOVT|
                NR-GSERI|GOVTB|GOVTC1|GOVTO2|GOVTA2|GOVTAB|25GP1BP|25GP1RP|62GGO1BN|GGOV0A|63GO1HN|GOVTR|25IDXBN|25IDXRN|GOVTQ|GOVTN|GOVTP|25GOVBN|25GVDBN|
                22COPBN|25GOVFN|25GO1FN|25MUYFN|25ADPFN|GOVTD2|25GVDFN|25GO1FP|25GOVHN|25GVDHN|25GOVRN|25GVDRN|GOVTEN|GOVTT|25SUMBN|25SS1BN|25SUMFN|25SS1FN|
                25SUMHN|25SS1HN|25SUMRN|25SR1RN|GOVTSP|GOVTF|25SU1BN|GOVPB|25GWOBN|WINTERGOVTFB|WINTERGOVTHB|WINTERGOVT|WINTERFB|WINTERHB|WINTERGOVTB|25WINBN|
                25WINFN|25WINHN|25WINRN|EMBASB|63GO1BP|63GO2FP|63GO1FP|63GO1HP|63GO3FP|63GO1FN|63GO1RP|63GO1BN|63GO1RN|63GO2BN|63GO2BP|63GO2FN|63GO2HP|63GO2RN|
                63GO3FN|63GO4BP|63GO4FP|63GO4HP|63GO4RP|63GO5FP|63GO6FP|57HOMRP|GOVBAR|25HOMRP|68TRVBN|ADV21|CONLNLB|DWDINE|MKTLOV|62SMIBN|62SMIHN|25USOBN|
                63GOVBN|25GOJBN|25GOFHN|25GOFRN|25GOPBP|22CHSBP|22CLEBP|22CLFBP|22CLGBP|22CLHBP|22CLIBP|22CLJBP|22CLKBP|22CLMBP|22CLNBP|22CLOBP|22CLQBP|
                22CLSBN|22CLZBP|22CODHN|22COKBN|22COMBN|22CONBN|22COOBN|22DANBP|22SEBBP|37WHGBN|37WHGRN|25FCHBP|63GOHBP|63GOBBP|63GOFBP|63GC0FP|57AD2RP|
                57AD2FP|25GOBBP|25GOVBP|63GC1RP|63GC4BP|63GC2BP|63GC3BP|25FGABN|25FGABP|63GVCBP|63GVFBP|57AD8FP|25PMABN|22CVEBP|22CVERP|51DVMBN|63GOVBP|
                25GVTBP|25GVTRP|63MOSBP|63DRCBP|22CLABP|22CLARP|22CLCBP|54STFRN|DBELC1|IPPOINTS|IPRNP`;
    }

    const customUserRatesPattern = {
      1039974:
        `^(MIL|MILT|MILITARY|HERO|SAVY|DISAAA|NEXTM10WEB|DISAARP|VADVR|CAA|AAA|AAA([0-9]+)?|ARP|AARP|AARP([0-9]+)?|SS|SNR|SRS|SSR|SENIOR|GM|SG|GOV[^\\s]*
            |FGOV|SGOV|STAT|GOVERNMENT|LEMART|INDUSTRY|DIAMOND|OH10|MKT14ADV|MTKIPM|MO10|IMGOV|REDEM|STAYCA${extraBlacklistRates})$`,
      1036882:
        `^(MIL|MILT|MILITARY|HERO|SAVY|DISAAA|NEXTM10WEB|DISAARP|VADVR|CAA|AAA|AAA([0-9]+)?|ARP|AARP|AARP([0-9]+)?|SS|SNR|SRS|SSR|SENIOR|GM|SG|GOV[^\\s]*
            |FGOV|SGOV|STAT|GOVERNMENT|LEMART|INDUSTRY|DIAMOND|OH10|MKT14ADV|MTKIPM|MO10|IMGOV|REDEM|Promo[^\\s]*${extraBlacklistRates})$`,
      1032506:
        '^(MIL|MILT|MILITARY|HERO|SAVY|DISAAA|NEXTM10WEB|DISAARP|VADVR|CAA|AAA|AAA([0-9]+)?|ARP|AARP|AARP([0-9]+)?|SS|SNR|SRS|SSR|SENIOR|GM|SG|GOV[^\\s]*|FGOV|SGOV|STAT|GOVERNMENT|LEMART|INDUSTRY|DIAMOND|OH10|MKT14ADV|MTKIPM|MO10|IMGOV|REDEM|STAYCA)$',
      1039476:
        '^(MIL|MILT|MILITARY|HERO|SAVY|DISAAA|NEXTM10WEB|DISAARP|VADVR|CAA|AAA|AAA([0-9]+)?|ARP|AARP|AARP([0-9]+)?|SS|SNR|SRS|SSR|SENIOR|GM|SG|GOV[^\\s]*|FGOV|SGOV|STAT|GOVERNMENT|LEMART|INDUSTRY|DIAMOND|OH10|MKT14ADV|MTKIPM|MO10|IMGOV|REDEM|STAYCA)$',
    };

    const defaultRatesBlacklistPattern = `^(MIL|MILT|MILITARY|HERO|SAVY|DISAAA|NEXTM10WEB|DISAARP|VADVR|CAA|AAA|AAA([0-9]+)?|ARP|AARP|AARP([0-9]+)?|SS|SNR|SRS|SSR|SENIOR|GM|SG|GOV[^\\s]*
            |FGOV|SGOV|STAT|GOVERNMENT|LEMART|INDUSTRY|DIAMOND|OH10|MKT14ADV|MTKIPM|MO10|IMGOV|REDEM|Promo[^\\s]*|STAYCA${extraBlacklistRates})$`;

    return customUserRatesPattern[thn.data.accountUserId]
      || excludeRatesHotels[thn.data.propertyId] || defaultRatesBlacklistPattern;
  };

  engine._isBookingEngineDesktop = function _isBookingEngineDesktop(baseUrl) {
    const url = new URL(baseUrl);

    return !!(url.query?.hotel || url.query?.Hotel || url.searchParams?.get('hotel') || url.searchParams?.get('Hotel'))
      && (((url.query?.arrive || url.query?.Arrive || url.searchParams?.get('arrive') || url.searchParams?.get('Arrive'))
        && (url.query?.depart || url.query?.Depart || url.searchParams?.get('depart') || url.searchParams?.get('Depart')))
        || url.query?.chain || url.query?.Chain || url.searchParams?.get('chain') || url.searchParams?.get('Chain'));
  };

  engine._isBookingEngineMobile = function _isBookingEngineMobile(
    baseUrl,
    globalTokens,
  ) {
    const url = new URL(baseUrl);

    return !!url.host.match('/mobile.synxis.com/')
      && url.path.match('/SearchResults/Index') && globalTokens;
  };

  /**
   * @memberOf module:bs_6
   * @see {@link languageCheck}
   *
   * @param {object} ctx - The context object.
   */
  engine.languageCheck = function languageCheck(ctx) {
    const dataLayer = window.dataLayer;
    const $ = window.jQuery;
    const grandExcelsiorLangDic = {
      Italian: 'it',
      German: 'de',
      French: 'fr',
      Spanish: 'es',
      Russian: 'ru',
      'Chinese (Traditional)': 'zh',
    };

    return new Promise(function (resolve, reject) {
      let aux;
      const _GlobalTokens = window._GlobalTokens || window._globalTracking;

      const isBookingEngine = engine._isBookingEngineDesktop(window.location.href)
        || engine._isBookingEngineMobile(window.location.href, _GlobalTokens);

      if (!isBookingEngine && isGrandHotelExcelsior) {
        const lang = grandExcelsiorLangDic[window.gpgName];
        if (lang && ctx.langs.test(lang)) {
          resolve(lang);

          return;
        }
      }

      // Hotel Estelar
      if (thn.data.accountUserId === 1033828 && window.location.hostname.match(/hotelesestelar/)) {
        const hotelEstelarLang = document.querySelector('.gt-lang-code')?.textContent;
        if (hotelEstelarLang && ctx.langs.test(hotelEstelarLang)) {
          resolve(hotelEstelarLang);

          return;
        }
      }

      // le Narcisse Blanc
      if (thn.data.propertyId === 1011996) {
        aux = document.querySelector('.wg-dd-1-togle[lang]')?.getAttribute('lang');
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);
          return;
        }
      }

      // ESPACIO the Jewel of Waikiki
      if (thn.data.propertyId === 1037726) {
        const japaneseHostname = window.location.hostname.match(/\.jp/);
        if (japaneseHostname) {
          resolve('ja');
        } else {
          resolve('en');
        }
        return;
      }

      // Icelandair hotels - Icelandic lang is showed as english
      if (thn.data.accountUserId === 1035537) {
        const lang = document.querySelector(
          '[aria-labelledby=language-selection]',
        );
        const icelandic = lang && lang.options[lang.selectedIndex]?.textContent;
        if (icelandic && icelandic.match(/slenska/i)) {
          resolve('is');

          return;
        }
      }

      if (thn.data.propertyId === 1027460) {
        const lang = document.querySelector('input[name=lang]')?.value
          ?.toLowerCase();
        if (lang && ctx.langs.test(lang)) {
          resolve(lang);

          return;
        }
      }

      if (isFirstHotels) {
        const lang = document.documentElement.lang.slice(0, 2).toLowerCase();
        // Language mapping for 'nb' (Norwegian) and 'se' (Swedish)
        const langMappings = {
          nb: 'no',
          se: 'sv',
        };

        // Set firstHotelsLang based on language mapping or use lang directly
        const firstHotelsLang = langMappings[lang] || lang;
        // Check if firstHotelsLang is a valid language in the ctx.langs test
        if (firstHotelsLang && ctx.langs.test(firstHotelsLang)) {
          // If valid, resolve the promise with firstHotelsLang
          resolve(firstHotelsLang);
          return;
        }
      }

      /**
       * This case is for all the Synxis implementations that stores its language code inside an object
       * called 'window._globalTracking', and thn.dataLayer doesn't update correctly when the language changes
       * Hotels using it: Hardrock Hotels, Country Club Lima, Aranwa Hotels, ES Princep
       */
      if (
        isBookingEngine && !thn.empty(window._globalTracking)
        && !thn.empty(window._globalTracking.LangCode)
      ) {
        aux = (window._globalTracking.LangCode || 'en').substr(0, 2)
          .toLowerCase();
        if (aux && ctx.langs.test(aux)) {
          resolve(aux.toLowerCase());

          return;
        }
      }

      /**
       * This case is for all the Synxis implementations that stores its language code inside an object
       * called 'window._globalTokens', and thn.dataLayer doesn't update correctly when the language changes
       * Hotels using it: Bold Hotels,
       */
      if (
        isBookingEngine && !thn.empty(window._GlobalTokens)
        && !thn.empty(window._GlobalTokens.LangCode)
      ) {
        aux = window._GlobalTokens.LangCode?.substr(0, 2)?.toLowerCase();

        if (
          thn.data.propertyId === 1025463
          && aux?.match(/nb/i)
        ) { // Norefjell Ski & Spa engine
          aux = 'no';
        }

        if (aux && ctx.langs.test(aux)) {
          resolve(aux.toLowerCase());

          return;
        }
      }

      // Specific cases
      // Hard Rock Maldives
      if (
        thn.data.propertyId === 1016827
        && !window.location.hostname.match(/synxis/)
      ) {
        const pathname = window.location.pathname;
        const pathLang = (pathname && pathname.match(/\/maldives\/(\w{6,8})+/)) || '';
        const languages = {
          german: 'de',
          chinese: 'zh',
          russian: 'ru',
          arabic: 'ar',
        };

        aux = pathLang && pathLang[1] && languages[pathLang[1]];
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      if (window.location.hostname === 'reserva.hardrockhotelpuntacana.com') {
        // 1013240 Hard Rock Hotel & Casino Punta Cana - MX version
        resolve('es');
        return;
      }

      if (
        thn.data.propertyId === 1015750
        && !window.location.hostname.match(/synxis/)
      ) {
        aux = document.querySelector('[hreflang]')?.getAttribute('hreflang');
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      // 25 Hours Hotel
      if (thn.data.chainId === 1003473) {
        aux = document.querySelector('.lang_dropdown > div')?.getAttribute(
          'data-lang-code',
        )?.slice(0, 2);
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      // Prince Waikiki
      if (thn.data.propertyId === 1046038) {
        aux = document.querySelector('button.header__language-btn')?.textContent?.toLowerCase()?.trim();
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);
          return;
        }
      }

      // Maison favart
      if (thn.data.propertyId === 1012756) {
        aux = document.querySelector('.wg-dd-1-togle')?.textContent;
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);
          return;
        }
      }

      // East Hotels
      if (thn.data.accountUserId === 1050365) {
        aux = document.documentElement.lang || window.location.pathname.match(/\w{2}/)?.[0];
        if (aux === 'tc') {
          aux = 'zh_TW';
        }

        if (aux === 'sc') {
          aux = 'zh';
        }

        if (aux && ctx.langs.test(aux)) {
          resolve(aux);
          return;
        }
      }

      // Hotel Chapter Roma
      if (thn.data.propertyId === 1039022) {
        aux = thn.parseUrlParameter('lang') || 'en';
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      if (window.location.hostname === 'es.hardrockhotelloscabos.com') {
        // 1016259 - Spanish version always indicates <html lang="en" .. >
        resolve('es');
        return;
      }

      if (
        window.location.hostname === 'mareabeachfrontvillas.com'
        && document.querySelector
      ) {
        if (document.querySelector('#menu-espanol')) {
          resolve('es');
        }
        // Resolve english because the webpage always indicate  <html lang="es-ES"
        resolve('en');
        return;
      }

      // Sublime Samana
      if (thn.data.propertyId === 1014277) {
        const lang = document.querySelector('.balix')?.textContent;
        if (lang && lang.match(/inglés|ingles/i)) {
          resolve('es');

          return;
        }
      }

      // Country Club Lima Hotel
      if (thn.data.accountUserId === 1034292) {
        const lang = document.querySelector('.active')?.textContent;
        if (lang.length && lang === 'ENG') {
          resolve('en');
        } else {
          resolve('es');
        }

        return;
      }

      // Aranwa
      if (thn.data.accountUserId === 1033812) {
        aux = $('#qts-lang-menu option:selected').text();
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      if (window.location.hostname.match(/^(www.)?hotelesemporio.com$/)) {
        // All home pages with this hostname indicate a wrong lang in the html tag
        // Always resolve 'es' (www.emporiohotels.com is the 'en' version)
        resolve('es');

        return;
      }

      // Thurnher’s Alpenhof hotel
      if (
        (thn.data.propertyId === 1027032)
        && window.location.hostname.match(/thurnhers/)
      ) {
        aux = window && window.Drupal && window.Drupal.settings
          && window.Drupal.settings.pathPrefix;
        if (aux && aux.match(/en/)) {
          resolve('en');
        } else {
          resolve('de');
        }

        return;
      }

      // Synxis Mobile version
      if (window.location.hostname === 'mobile.synxis.com') {
        resolve(
          (window.dataLayer && thn.arrayGet(dataLayer, 'SynxisLanguage'))
          || 'en',
        );
      }

      // Synxis Regular Desktop version
      const languageFromPath = window.location.pathname.match(ctx.urlRe);
      if (
        languageFromPath?.[1] && (ctx.langs.test(languageFromPath[1]) || 'jp')
      ) {
        resolve(languageFromPath[1]);

        return;
      }

      // Weitzer & Eremito
      if (
        [1035221, 1039023].includes(thn.data.propertyId)
        && window.location.hostname.match(/(be.synxis.com)/)
      ) {
        aux = window && window.location && window.location.search
          && window.location.search.match(/locale=[a-z]{2}/)[0].split('=')[1];
        if (ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      if (thn.data.propertyId === 1016976) {
        if (window.location.pathname.match(/\/en\//)) {
          resolve('en');
        } else {
          resolve('de');
        }
      }

      aux = thn.dataLayerHelper.get('LangCode');
      aux = aux && aux.substr(0, 2);
      if (
        window.location.hostname.match(
          /(be.synxis.com|book.viennahouse.com)/,
        )
        && ![1035398, 1035423].includes(thn.data.accountUserId)
        && aux && ctx.langs.test(aux)
      ) {
        resolve(aux);

        return;
      }

      if (thn.data.propertyId === 1010915 && window.location.pathname === '/') {
        // Berlovento Home has lang='en' but it's only in spanish (weird...)
        resolve('es');
        return;
      }

      // 'Habita Hoteles' and their main homes
      if ([1011093, 1011065, 1011066].includes(thn.data.propertyId)) {
        if (window.location.pathname === '/main.html') {
          resolve('en');
        } else {
          resolve('es');
        }

        return;
      }

      // Continental Hotels
      if (thn.data.accountUserId === 1032579) {
        resolve('ro');

        return;
      }

      // Aryaduta Hotels
      aux = thn.data.accountUserId === 1032691
        && window.location.pathname.match(/-(\w{2})\.html$/);
      if (aux && ctx.langs.test(aux[1])) {
        resolve(aux[1]);

        return;
      }

      // Fraserschina
      if (
        thn.data.accountUserId === 1033157 && window.addthis_config
        && window.addthis_config.ui_language === 'cn'
      ) {
        resolve('zh');

        return;
      }

      // Eden Hotel Wolf
      if (
        thn.data.propertyId === 1016665
        && window.location.hostname.match(/eden-hotel-wolff/)
      ) {
        const edenHotelWolfLang = document.querySelector('.nav.navbar-nav')
          ?.textContent?.match(/[A-Z]{2}/)?.[0].toLowerCase();
        if (edenHotelWolfLang && ctx.langs.test(edenHotelWolfLang)) {
          resolve(edenHotelWolfLang);

          return;
        }
      }

      if (
        thn.data.propertyId === 1025478
        && window.location.hostname.match(/m\.es\.hrhpanamamegapolis\.com/)
      ) {
        // In the mobile version of Hard Rock Hotel Panama Megapolis language is located in the subdomain
        resolve('es');

        return;
      }

      // Ex: https://www.dusit.com/dusitthani-guamresort/zh-hans/
      if (
        thn.data.accountUserId === 1033433
        && window.location.hostname.match(/\.dusit\.com$/)
      ) {
        const dusitUrlLang = window.location.pathname.match(
          /\/(\w{2}(-\w{2,4})?)\//i,
        );
        aux = dusitUrlLang && dusitUrlLang.length > 1 && dusitUrlLang[1];
        aux = aux && aux.substr(0, 2);
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      // Hotel Gustave
      if (thn.data.propertyId === 1026761) {
        aux = (window.klaroConfig && window.klaroConfig.lang)
          || document.querySelector('[id^="page-"]')?.getAttribute('lang');

        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      // Hotel Domaine de Joinville
      if (thn.data.propertyId === 1013878) {
        resolve('fr');

        return;

      }

      // Hoteles Click-Clack
      if (
        (thn.data.accountUserId === 1036777)
        && window.location.hostname.match(/clickclackhotel/)
      ) {
        const english = document.querySelector('a[title="Español"]');
        // When the link shows 'es' the website is in English, and opposite
        aux = english ? 'en' : 'es';

        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      // Caesars Palace Dubai
      if (thn.data.accountUserId === 1039844) {
        const language = document.querySelector('#languageNav span')
          ?.textContent;
        const languages = {
          English: 'en',
          العربية: 'ar',
          简体中文: 'zh',
          Deutsch: 'de',
          Русский: 'ru',
        };

        aux = language && languages[language];
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      // Hotel Princesa Plaza
      if (thn.data.propertyId === 1050080) {
        const language = document.querySelector('.header__language-button')?.textContent?.toLocaleLowerCase();
        if (language && ctx.langs.test(language)) {
          resolve(language);

          return;
        }
      }

      // Grand Hotel Huis Ter Duin
      if (
        thn.data.propertyId === 1034152 && document.querySelector('#myBooking')
      ) {
        const language = thn.parseUrlParameter('lang');
        if (language && ctx.langs.test(language)) {
          resolve(language);

          return;
        }

        resolve('nl');
        return;
      }

      // Hotel Napoleon
      if ((thn.data.propertyId === 1024366)) {
        const hotelNapoleonLang = document.querySelector(
          '.nav_menu-language-switcher [lang]',
        )?.getAttribute('lang');
        if (hotelNapoleonLang && ctx.langs.test(hotelNapoleonLang)) {
          resolve(hotelNapoleonLang);

          return;
        }
      }

      // Onyx Hospitality - this is a special case, the language should be always 'en'
      if (isOnyxHospitality) {
        resolve('en');
        return;
      }

      aux = document.documentElement.lang;
      if (aux) {
        if (aux.length > 2) { aux = aux.substr(0, 2); }
        if ((thn.data.propertyId === 1025463) && aux.match(/nb/i)) { aux = 'no'; } // Norefjell Ski & Spa homepage
        if ((thn.data.propertyId === 1158177) && aux.match(/ho/)) { aux = 'en'; } // Steveston Waterfront Hotel
        if (aux && ctx.langs.test(aux)) {
          resolve(aux);

          return;
        }
      }

      aux = _GlobalTokens
        && (_GlobalTokens.LangCode || window._locale)?.split('-')?.[0];

      if (aux && ctx.langs.test(aux)) {
        resolve(aux);

        return;
      }

      reject('no_language');
    });
  };

  /**
   *
   * @memberOf module:bs_6
   * @see {@link allowedLocation}
   *
   * @param {object} ctx - The context object.
   */
  engine.allowedLocation = function allowedLocation() {
    let isAllowedLocation = true;

    if (
      thn.data.accountUserId === 1032990 // Outrigger
      // block execution for outrigger B2B Program
      && thn.parseUrlParameter('ma') === 'outriggerbusinessconnection.com'
    ) {
      isAllowedLocation = false;
    }
    // Hard Rock Hotel San Diego, Laguna Brisas, Makeready Hotels(UID:1042047)
    if (
      ([1024912, 1026146].includes(thn.data.propertyId)
        || [1042047].includes(thn.data.accountUserId))
      // block execution for all group codes
      && (document.querySelector('[class*=codes_appliedPromoGroupCorp]')
        || window.location.search.match(/group=*[a-zA-Z0-9]+/)
        || (window._GlobalTokens && window._GlobalTokens.GroupCode))
    ) {
      isAllowedLocation = false;
    }

    return isAllowedLocation;
  };

  /**
   * @memberOf module:bs_6
   * @see {@link isUserLogged}
   *
   * @param {object} ctx - The context object.
   */
  engine.isUserLogged = function isUserLogged(ctx) {
    // Standard
    const loginEl = document.querySelector('.myaccount-container_user');
    const regexList = /(Sign In|Logg|登入|登录|サインイン|ลงชื่อเข้าใช้|تسجيل الدخول|כניסה|ΣΥΝΔΕΣΗ|Přihlásit se|OTURUM AÇ|ĐĂNG NHẬP|LOGGA IN|KIRJAUDU|로그인|ACCEDI|BEJELENTKEZÉS|AANMELDEN|Inicio de|Masuk|Log ind?|ANMELDEN|SISSELOGIMINE|INICIAR|CONNEXION|PRIJAVA)/i; // the default elements, when nobody is logged
    let isLogged = false;

    // Contemplate Hotels that use React but with a different Log in class in the homepage
    const hasDifferentClassInHP = window.location.hostname.includes(
      'centarahotelsresorts',
    );

    if (engine._isReact()) {
      if (hasDifferentClassInHP) {
        isLogged = !!document.querySelector('.post-login_info');

        return isLogged;
      }
      // If the login element contains the default element, nobody is logged
      if (loginEl?.textContent?.match(regexList)) {

        return false;
      }
      // Oberoi Hotels
      if (thn.data.accountUserId === 1034262) {
        isLogged = !thn.isVisible(document.querySelector('#oberoioneCTA'));

        return isLogged;
      }
      // Otherwise, whatevar the react state instructs
      isLogged = !document.querySelector(
        '[aria-controls="myaccount-container-login"]',
      );

      return isLogged;
    }

    // Flora Hospitality Group
    if (thn.data.accountUserId === 1033439) {
      // In the BE check the queryvar "shelltype"
      if (window.location.hostname.slice(-10) === 'synxis.com') {
        isLogged = !window.location.search.match(/shelltype=nonmember/);
      } else if (
        document.querySelectorAll // Hotel Home page
        && document.querySelectorAll('.header .profilePanel').length
      ) {
        isLogged = true;
      }
    }

    // Barcelo
    if (engine._isBarcelo()) {
      isLogged = !document.querySelector('.noUser');
    }

    // Hotel Napoleon
    if (thn.data.accountUserId === 1035313) {
      isLogged = !!document.querySelector('#logout_header');
    }
    // Preferred Hotels
    if ([1001591, 1037917, 1037718].includes(thn.data.accountUserId)) {
      isLogged = !!document.querySelector('.login-name');
    }
    // My Place Hotels
    if (thn.data.accountUserId === 1038905) {
      isLogged = !!document.querySelector('.logout');
    }
    // Oaks Hotels
    if (thn.data.accountUserId === 1032769) {
      isLogged = !document.querySelector('#confirmLoggedIn');
    }
    // First Hotels
    if (isFirstHotels) {
      const isBookingEngine = window.location.hostname.match(/synxis.com/);
      isLogged = isBookingEngine
        ? thn.parseUrlParameter('rate') === 'MEMBER'
        : thn.objectGet(window.dataLayer, 'LoggedInUser') === 'Yes';
    }

    return isLogged;
  };

  /**
   * @memberOf module:bs_6
   * @see {@link thn.isNolog}
   */
  engine.isNolog = function isNolog() {
    if (engine._isBarcelo()) {
      // Signal as "nolog" visit when the user is a Barceló employee
      // If the user is not an employee 'cp.user_test' won't exist,
      // otherwise will have the value "1"
      const isBarceloEmployee = window.utag_data
        && window.utag_data['cp.user_test'] === '1';
      return !!isBarceloEmployee;
    }

    return false;
  };

  engine._isReact = function _isReact() {
    const isReact = window.React
      || ((((window || {}).process || {}).env || {}).APPLICATION_URL
        || window.location.hostname).match(/be\.synxis\.com/);

    return !!isReact;
  };

  /**
   * Do some action only after "loading" has ended.
   * @param {function} callback
   */
  engine._afterLoading = function _afterLoading(callback) {
    // Work will be done, once the observer sees the page as "not loading".
    // Create a place to store the callbacks
    if (!thn.data.synxisReactAfterLoadingCb) {
      thn.data.synxisReactAfterLoadingCb = [];
    }

    // Dedupe callbacks
    if (thn.data.synxisReactAfterLoadingCb.includes(callback)) {
      return;
    }

    // Add the callback to the queue
    thn.data.synxisReactAfterLoadingCb.push(callback);

    // If we are already observing, all is done
    if (thn.synxisReactLoadingObserve) {
      return;
    }

    // Otherwise:
    // 1 - create the observer
    thn.synxisReactLoadingObserve = thn.debounce(function () {
      if (
        !thn.data.synxisReactAfterLoadingCb
        // This observer will do nothing while the page "is loading"
        || engine._isLoading()
      ) {
        return;
      }

      // Once the page is not loading ...
      // Do the real work in order
      let cb = thn.data.synxisReactAfterLoadingCb.shift();
      while (cb) {
        cb();
        cb = thn.data.synxisReactAfterLoadingCb.shift();
      }

      // And stop the observation
      thn.stopObserveDom(document.body, thn.synxisReactLoadingObserve);
      delete thn.synxisReactLoadingObserve;
      delete thn.data.synxisReactAfterLoadingCb;
    }, 600);

    // 2 - Observe the page
    thn.observeDom(document.body, thn.synxisReactLoadingObserve, {
      childList: true,
      subtree: true,
      characterData: false,
      attributes: false,
    });

    // 3 - Set up the cleaning
    thn.pageUnload(function () {
      if (thn.synxisReactLoadingObserve) {
        thn.stopObserveDom(document.body, thn.synxisReactLoadingObserve);
        delete thn.synxisReactLoadingObserve;
        delete thn.data.synxisReactAfterLoadingCb;
      }
    });
  };

  /**
   * If the page is in a "loading" stage.
   * @return bool
   */
  engine._isLoading = function _isLoading() {
    // Not implemented for other versions than react
    if (!engine._isReact()) {
      return false;
    }

    // On react version, always that we have an "spinner"
    // visible in the screen, then we consider the page as loading
    return Array.from(document.querySelectorAll('.spinner_main'))
      .filter(thn.isVisible).length > 0;
  };

  /**
   * Actions to be done "onStateChange" + loading ended.
   */
  engine._onStateChange = function _onStateChange(ctx) {

    if (thn.data.pageName === 'Rooms and Rates') {
      // Check for a currency change
      const formerCurrency = thn.widgets.price.data.currency;
      const currentCurrency = thn.parseUrlParameter('currency');
      if (formerCurrency !== currentCurrency) {
        // If currency has changed (we should be on RnR, right?)
        // so try to refresh the PW
        if (thn.widgets.price.refresh() && !thn.widgets.price.isSet()) {
          thn.widgets.price.hide();
        }
      }

      if (isSeasidePalmBeach && thn.parseUrlParameter('segment') !== 'BB') {
        thn.widgets.price.destroy();
      }
    }

    // Also, recheck the language. In case the language didn't change
    // then recheck the page. If the page didn't change neither,
    // and we are on RnR, try to refresh the PW
    thn.languageCheck().then(function ({ changed }) {
      if (!changed) {
        {
          engine._checkReservationHold();
        }

        setTimeout(() => {
          thn.pageCheck();
          if (thn.data.pageName === 'Rooms and Rates') {
            // Carneros Resort and Spa : we have to hide the PW when filters are applied.
            if (isCarnerosResort) {
              // check all possible filter
              const roomParam = thn.parseUrlParameter('room');
              const rateParam = thn.parseUrlParameter('rate');
              const roomCategoryParam = thn.parseUrlParameter('roomcategory');
              const filterBar = document.querySelector('.filter-bar_tagGroup');
              const availabilityFilters = thn.objectGet(
                window.dataLayer,
                'AvailabilityFilters',
                { first: false },
              );
              const features = availabilityFilters?.features
                || availabilityFilters?.feature;
              const bedType = availabilityFilters?.bedType;
              const filterApplied = roomParam || rateParam
                || roomCategoryParam || filterBar || features || bedType;
              if (filterApplied) {
                thn.widgets.price.destroy();
              } else {
                thn.widgets.price.refresh();
              }
            } else if (
              isAmanHotels && ['mobile'].includes(thn.data.media.name)
            ) {
              thn.widgets.price.update();
            } else if (!usersOnlyWantToShowFirstRate) {
              thn.widgets.price.refresh();
            }
          }
        }, 1000);
      }
    });

    thn.debounce(thn.pageCheck, 600)();
  };

  /**
   * @see {@link onStateChange}
   *
   * @param {object} ctx - The context object.
   */
  engine.onStateChange = function onStateChange(ctx) {
    // The function _onStageChange is the one that makes the real work
    // Call it "after loading"
    engine._afterLoading(engine._onStateChange);
    if (
      thn.data.accountUserId === 1035498
      && window.location.hostname === 'www.grandhotelbellevuelondon.com'
    ) {
      setTimeout(thn.pageCheck, 400);
    }
  };

  /**
   * @memberOf module:bs_6
   * @see {@link generateSearchUrl}
   *
   * @param {object} ctx - The context object.
   */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {
    const url = new URL(ctx.baseUrl);
    const _GlobalTokens = window._GlobalTokens || window._globalTracking;
    const isMobile = engine._isBookingEngineMobile(ctx.baseUrl, _GlobalTokens);
    const languageCodes = {
      sv: 'sv-SE',
      da: 'da-DK',
      en: 'en-US',
      no: 'nb-NO',
      es: 'es-MX',
      de: 'de-DE',
      fr: 'fr-FR',
      it: 'it-IT',
    };

    /* CASES:
        CLASSIC
        https://gc.synxis.com/rez.aspx?Hotel=64982&Chain=5154&template=GCF&shell=charlie&lang=1&start=availresults&arrive=2%2F07%2F2018&depart=2%2F08%2F2018&adult=2&currency=usd
        https://gc.synxis.com/?Hotel=1362&template=RES_Tortue_Hamburg_V1&shell=RES_Tortue_Hamburg_V3&locale=en-EN&src=tortue&start=mobavailresults&_ga=2.153665208.1035289919.1575544812-679819328.1575544812&arrive=2020-01-14&depart=2020-01-16&adult=1&Promo=TORTUE2020

        REACT
        https://be.synxis.com/?_ga=2.109333826.618519508.1575910297-755912043.1575455680&adult=2&arrive=2019-12-09&chain=8565&child=0&currency=USD&depart=2019-12-10&hotel=9626&level=hotel&locale=en-US&rooms=1

        MOBILE
        https://mobile.synxis.com/SearchResults/Index?arrive=2020-2-1&depart=2020-2-5&adult=2&start=mobavailresults&hotel=12300&chain=15564
      */

    // This condition is necessary for this property because the lastSearch data of the booking engine before migration is also among the candidate URLs, and even if it is not, the URL of the homepage is applied first.
    const propertiesBlackList = [
      1037303,
      1040793,
      1036989,
      1036990,
      1036991,
      1051668,
      1047255,
      1024390,
      1163111,
    ];
    const usersBlackList = [1042715, 1041836, 1047208];
    if (
      (propertiesBlackList.includes(thn.data.propertyId)
        || usersBlackList.includes(thn.data.accountUserId))
      && !url.hostname.match(/synxis|reservations|bookings/)
    ) {
      ctx.newUrl = '';

      return ctx;
    }

    const childrenAges = ctx?.data?.roomsDispo?.[0]?.childrenAges || [];
    // Assign new values into lowercase variables, and remove uppercase ones if present, some hotels use both, but only the lowercased ones seem to work.
    url.searchParams.set('arrive', '[date_begin]');
    url.searchParams.set('depart', '[date_end]');
    url.searchParams.set('currency', '[currency]');
    url.searchParams.set('adult', '[adults]');
    url.searchParams.set('child', '[children]');
    url.searchParams.set(
      'start',
      isMobile ? 'mobavailresults' : 'availresults',
    );
    url.searchParams.set('locale', '[language]');
    if (childrenAges.length) {
      url.searchParams.set('childages', childrenAges.join('|'));
    }

    const hotelId = (_GlobalTokens && _GlobalTokens.HOTEL_ID)
      || thn.dataLayerHelper.get('HOTEL_ID');
    const chainId = (_GlobalTokens && _GlobalTokens.CHAIN_ID)
      || thn.dataLayerHelper.get('CHAIN_ID');

    if (
      (!url.searchParams.get('hotel') || !url.searchParams.get('Hotel'))
      && hotelId
    ) {
      url.searchParams.set('hotel', hotelId);
      url.searchParams.delete('Hotel');
    }
    if (
      (!url.searchParams.get('chain') || !url.searchParams.get('Chain'))
      && chainId
    ) {
      url.searchParams.set('chain', chainId);
      url.searchParams.delete('Chain');
    }
    // Delete path to avoid pages like '/addOns' unless it's the QRES version and it has the following path already:
    if (
      !isMobile && !(url.host.match(/qres.qr-hotels.com/)
        && (url.path && url.path === '/be/rez.aspx'))
    ) {
      url.path = '/';
    }
    // Remove promocode parameters that may exist in the URL
    url.searchParams.delete('Promo');
    url.searchParams.delete('promo');
    url.searchParams.delete('coupon');
    // If we define a promocode param on "property.settings",
    // this snippet will force to use the defined property promocode parameter
    // instead of the promocode params defined in "bookingSystem.promocodeVars"
    const promocodeParameter = thn.getSetting('engine_promocode_parameter_name');
    if (promocodeParameter) {
      // Make sure the default variable is not present.
      url.searchParams.set(promocodeParameter, '');
    }
    // In some URLs, we have the param "submit" that prevent us applying the 1click-promocode when having invalid values
    // It can have values like "Book now", where makes the URL invalid, we need to delete this param
    url.searchParams.delete('submit');
    if (
      thn.data.propertyId === 1012756
      && (url.hostname !== 'reservation.lamaisonfavart.com'
        || !url.searchParams.has('hotel'))
    ) {
      ctx.newUrl = '';
      return ctx;
    }

    // export into ctx.newUrl
    ctx.newUrl = url.toString();
    // Change all the blank spaces by '+' signs if the new URL is not empty
    if (ctx.newUrl) {
      ctx.newUrl = ctx.newUrl.replace(/\s+/g, '+');
    }

    if (url.hostname.startsWith('www')) {
      ctx.newUrl = '';
      return ctx;
    }
    // Format of dates: yyyy/mm/dd -> yyyy-mm-dd
    ctx.newData.dateBegin = ctx.data.dateBegin?.replace(/\//g, '-');
    ctx.newData.dateEnd = ctx.data.dateEnd?.replace(/\//g, '-');
    // format language
    ctx.newData.language = languageCodes[ctx.newData.language]
      || languageCodes[ctx.data.language] || 'en-US';

    return ctx;
  };

  /**
   * @memberOf module:bs_6
   * @this thn.widgets.price
   * @see {@link Price#observeDom}
   *
   * @param {object} ctx - The context object.
   */
  engine.priceObserveDom = function priceObserveDom(ctx) {
    const $ = window.jQuery;
    const mobileVersion = window.location.hostname.match(/^mobile\./);
    const dataLayer = window.dataLayer;
    let vId;
    if (mobileVersion) ; else if (!engine._isReact()) {
      // Regular Desktop version
      vId = $ && $('[id^=V151_C3_]').length ? 'V151' : 'V111';
      if (vId === 'V151') {
        $(document).off(
          'click',
          '.ViewProductsBtn, .CompareBtnBtm',
          ctx.widget.minimize,
        );
        $(document).on(
          'click',
          '.ViewProductsBtn, .CompareBtnBtm',
          ctx.widget.minimize,
        );
      }
    } else if (
      !thn.synxisGeneralObserve
      && (thn.empty(dataLayer) || !thn.synxisGeneralObserveDatalayer)
    ) {
      // React Version -  be.synxis.com
      /**
       * We should use "synxisGeneralObserveDatalayer" from now on
       *
       * While using "synxisGeneralObserve" we can experience a pretty wierd case which is hard to replicate
       * In some cases, "synxis_general_observe" will make us parse the prices in the perioid between
       * the loader is being hidden and the prices are not rendered yet, this cause us to obtain the prices from
       * the datalayer objects that yet contains the prices from the previous search, not the one that is being loaded
       *
       * We cannot just delete "synxisGeneralObserve" becuase, not in all isntancies of synxis, we can find the event "rooms" of the datalayer
       * That's the event we need to tell the PW to refresh
       *
       * I suggest to just create both observers, and if we finally "use synxisGeneralObserveDatalayer" (we found a room event)
       * we can just delete the old observer inside the new one. The main problem is that we cannot guarantee
       * that the rooms event exists on the datalayer when priceObserveDom is called
       */

      // Old observer
      const selector = Array.from(document.querySelectorAll('.app_row')).pop();
      thn.synxisRoomUnavailable = function () {
        return !!document.querySelector(
          '.product-availability-container_unavailableMessage',
        );
      };

      if (selector) {
        let loading = false;
        thn.synxisGeneralObserve = thn.debounce(
          function thnSynxisGeneralObserve(mutations) {
            mutations.forEach(function (mutation) {
              if (document.querySelector('.spinner_main')) {
                loading = true;
                ctx.widget.hide(); // Here we may have already shown the PW
              } else if (
                mutation.type === 'childList'
                && !document.querySelector('.spinner_main') && loading
                && !document.querySelector('.calendar-flyout_container')
                && !document.querySelector('.guests-flyout_container')
              ) {
                loading = false;
                // Check if rates are displayed
                // If no rooms available is displayed, force update if app_row > 1
                ctx.widget.logger.debug('** DOM Changed **');
                if (ctx.widget.refresh() && !ctx.widget.isSet()) {
                  ctx.widget.hide();
                }
              }
            });
          },
          400,
        );

        thn.observeDom(selector, thn.synxisGeneralObserve, {
          childList: true,
          subtree: true,
          characterData: false,
          attributes: false,
        });
      }

      // New observer
      if (!thn.empty(dataLayer)) {
        thn.synxisGeneralObserveDatalayer = new thn.DataLayerHelper(
          dataLayer,
          function (model, message) {
            const dataLayerMessageEvent = (isAmanHotels || isMaiamiCoconutGrove || isCarnerosResort)
              ? 'rooms'
              : 'app';
            if (
              message.event === dataLayerMessageEvent
              || message.event === 'impressionGlobalReady'
              || message.event === 'impressionReady'
            ) {
              setTimeout(() => ctx.widget.refresh(), 300);
              // This instance of Synxis uses a suitable datalayer, we can delete the older observer
              if (thn.synxisGeneralObserve) {
                thn.stopObserveDom(selector, thn.synxisGeneralObserve);
                delete thn.synxisRoomUnavailable;
                delete thn.synxisGeneralObserve;
              }
            }
          },
          true,
        );
      }

      thn.pageUnload(function () {
        // Check if exists, could be deteled on synxisGeneralObserveDatalayer
        if (thn.synxisGeneralObserve) {
          thn.stopObserveDom(selector, thn.synxisGeneralObserve);
          delete thn.synxisRoomUnavailable;
          delete thn.synxisGeneralObserve;
        }
        // Check if exists, if we don't find a dataLayer, this function wont be defined
        if (thn.synxisGeneralObserveDatalayer) {
          thn.synxisGeneralObserveDatalayer.cancel();
          delete thn.synxisGeneralObserveDatalayer;
        }
      });

      // Property 1032305 shows a list of rooms when you enter for the first time but it has a "View More Rates" button that when you click it shows more rooms.
      if (thn.data.propertyId === 1032305) {
        const undelegate = thn.delegate(
          'click',
          '.thumb-cards_viewMore button',
          function () {
            thn.pageCheck({ force: true });
          },
        );
        thn.shutdown(undelegate);
      }

      const containerAside = document.querySelector('.app_row aside');

      if (containerAside && !usersOnlyWantToShowFirstRate) {
        refreshPriceByAside = thn.debounce(ctx.widget.refresh, 300);

        thn.observeDom(containerAside, refreshPriceByAside, {
          childList: true,
          subtree: true,
        });

        thn.pageUnload(() => {
          thn.stopObserveDom(containerAside, refreshPriceByAside);
          refreshPriceByAside.cancel();
          refreshPriceByAside = null;
        });
      }

      if (thn.data.propertyId === 1158344) {
        if (dataLayer) {
          const refreshPriceDl = ({ message }) => {
            if (message.event === 'view_item_list' || message.event === 'view_item') {
              ctx.widget.update();
            }
          };

          thn.bus.on('dataLayer:push', refreshPriceDl);

          thn.pageUnload(() => {
            thn.bus.detach('dataLayer:push', refreshPriceDl);
          });
        }
      }
    }
  };

  /**
   * Implements **noRooms**.
   *
   * @memberOf module:bs_6
   * @this thn.widgets.price
   * @see {@link Price#setData}
   *
   * @param {object} ctx - The context object.
   */
  engine.priceParseData = function priceParseData(ctx) {
    const widgetData = {};
    const dataLayer = window.dataLayer;
    const isClubQuartersHotels = thn.data.accountUserId === 1035339;
    const ecommerceRoomsInfo = thn.dataLayerHelper.get('ecommerce.impressions');
    const isMemberPromoRatesForClubQuarterHotels = isClubQuartersHotels && Array.isArray(ecommerceRoomsInfo) && !thn.empty(ecommerceRoomsInfo.map((el) => el.variant?.match(/^member rate/i)).filter(Boolean));
    const isAmanTest = isAmanHotels
      && thn.parseUrlParameter('data')?.includes('test');
    const blockPriceWidgetWhenGroups = [1044545, 1044543, 1044547, 1044546, 1044544].includes(thn.data.propertyId);
    const isLoaderPresent = !!document.querySelector('.reservation-cart-container_loaderContainer');
    const isFraserHotels = thn.data.accountUserId === 1033700;

    if (isLoaderPresent) {
      this.logger.warn('Loader is present, stopping PW execution');
      return widgetData;
    }

    if (isMemberPromoRatesForClubQuarterHotels) {
      this.logger.warn('Do not show the PW anytime a user comes from members-only page & rates');
      return widgetData;
    }

    if (
      isAmanTest || thn.parseUrlParameter('data')?.includes('test')
      || thn.parseUrlParameter('themecode')?.includes('BPROTheme')
      || (thn.data.accountUserId === 1039800
        && thn.parseUrlParameter('group')?.includes('AS524DORADO'))
    ) {
      this.logger.warn(
        'Block the PW to appear when a user comes with this parameter on the URL: themecode=BPROTheme and group=AS524DORADO(UID: 1039800) and Aman with test',
      );
      widgetData.set = false;

      return widgetData;
    }

    const propertyBlockMap = {
      1026803: { param: 'promo', value: 'AGA25', log: 'Block PW for promo AGA25' },
      1014595: { param: 'rate', value: 'EBRD', log: 'Block PW for rate EBRD' },
      // add here more parameters if needed
    };

    if (isFraserHotels && (thn.parseUrlParameter('group') !== '' || thn.parseUrlParameter('promo') !== '')) {
      this.logger.warn('Block the PW to appear when a user comes with group or promo on the URL');
      widgetData.set = false;
      return widgetData;
    }

    const blockRule = propertyBlockMap[thn.data.propertyId];
    if (blockRule && thn.parseUrlParameter(blockRule.param)?.includes(blockRule.value)) {
      this.logger.warn(blockRule.log);
      widgetData.set = false;
      return widgetData;
    }

    if (blockPriceWidgetWhenGroups && thn.parseUrlParameter('config')?.includes('GROUP')) {
      this.logger.warn('Block the PW to appear when a user comes with GROUP on the URL');
      widgetData.set = false;
      return widgetData;
    }

    // The URL is updated as soon as the user modifies the number of guests
    // Avoid parsing data when the search box is visible, the observer will auto-update once it's closed
    if (document.querySelector('.guests-flyout_container')) {
      this.logger.warn('Guests search box is in use, stopping PW execution');

      return widgetData;
    }

    if (
      isMaiamiCoconutGrove
      && !thn.objectGet(dataLayer, 'AvailableRooms', { first: false })
    ) {
      this.logger.debug('DATALAYER NOT FULLY LOADED');
      widgetData.set = false;

      return widgetData;
    }

    // I think having this hardcoded is mental & should not be hidden in a ONE-Liner
    const synxisCurrencyCodes = {
      0: 'HOTEL',
      1: 'USD',
      2: 'BMD',
      3: 'CAD',
      4: 'CHF',
      5: 'EUR',
      6: 'PHP',
      7: 'THB',
      8: 'AED',
      9: 'AUD',
      10: 'BRL',
      11: 'GBP',
      12: 'HKD',
      13: 'IDR',
      14: 'INR',
      15: 'JPY',
      16: 'KRW',
      17: 'MXN',
      18: 'MYR',
      19: 'NZD',
      20: 'PEN',
      21: 'RUB',
      22: 'SAR',
      23: 'SGD',
      24: 'TWD',
      26: 'ZAR',
      28: 'MTL',
      29: 'CNY',
      30: 'DKK',
      31: 'ISK',
      32: 'KWD',
      33: 'NOK',
      34: 'OMR',
      35: 'QAR',
      36: 'SEK',
      37: 'COP',
      38: 'TND',
      39: 'ALL',
      40: 'AFN',
      41: 'AMD',
      42: 'ANG',
      43: 'AOA',
      44: 'ARS',
      45: 'MAD',
      46: 'DOP',
      47: 'CRC',
      48: 'CLP',
      50: 'PYG',
      51: 'HTG',
      52: 'TTD',
      53: 'XCD',
      54: 'GYD',
      55: 'BSD',
      56: 'AWG',
      59: 'FJD',
      60: 'EGP',
      61: 'CZK',
      62: 'MZN',
      63: 'PLN',
      64: 'MOP',
      65: 'BGN',
      66: 'CUC',
      67: 'GNF',
      68: 'MGA',
      69: 'NIO',
      70: 'RON',
      71: 'SRD',
      72: 'TJS',
      73: 'TRY',
      74: 'UGX',
      75: 'CFA',
      77: 'KES',
      78: 'BHD',
      79: 'JOD',
      80: 'VEF',
      81: 'TMT',
      82: 'UAH',
      83: 'HRK',
      85: 'VND',
      86: 'GHS',
      87: 'NPR',
      88: 'HUF',
      89: 'VUV',
      90: 'ILS',
      91: 'AZN',
      92: 'KZT',
      93: 'GEL',
      94: 'PGK',
      95: 'SSP',
      96: 'SCR',
      97: 'BWP',
      98: 'MVR',
      99: 'NAD',
      100: 'LKR',
      101: 'TZS',
      102: 'MUR',
      103: 'IQD',
      104: 'NGN',
      105: 'PKR',
      106: 'YER',
      107: 'BZD',
      108: 'MNT',
      109: 'BAM',
      110: 'MDL',
      111: 'JMD',
      113: 'BBD',
      114: 'LBP',
      115: 'CFP',
      116: 'CVE',
      117: 'LAK',
      118: 'BND',
      119: 'MMK',
      120: 'SHP',
      121: 'RWF',
      122: 'ZMK',
      123: 'LSL',
      124: 'DJF',
      125: 'CDF',
    };

    const $ = window.jQuery;
    const _GlobalTokens = window._GlobalTokens || window._globalTracking;

    let price;
    let prices;
    let perNight;
    let nights;
    let roomDetails;
    let impressions;
    let priceWithRooms; // Final price includes all rooms
    const isSpinnerVisible = !!document.querySelector('.spinner_main');

    // IMPORTANT: From 2018-04-10 we DO NOT forbid PW execution on promocode presence anymore.
    // Please add exceptions for the problematical codes/hotels when needed.
    const promocodeBlacklist = [
      // requested by propertyId 1014188
      'CYBBAOFFER2',
      // 1014500 & 1014499
      'SUITEARRIVALS',
      // 1014500
      'SUMMERBA',
      'PKGESR',
      // 1014499
      'PKGSPA18',
      'PKGADV',
      // 1014432
      'FREECAR',
    ];
    const { promocode } = thn.widgets.price.getPromocode().promocode;
    // List here hotels which must NOT show PW with promocode presence
    const isPromocodeException = [].includes(thn.data.accountUserId)
      || [
        1014461,
        1014041, // Aria Hotel Budapest
        1015310, // Hotel de la tabletterie
        1014391, // Saguaro Palm Springs
        1014390, // Saguaro Scottsdale
        1025262, // The Gotham Hotel
      ].includes(thn.data.propertyId);

    const hardRockHotelSanDiegoPromoExclusion = thn.data.propertyId === 1024912
      && !!((thn.parseUrlParameter('promo') || promocode)?.match(
        /AAA|AARP|GOVBFST/,
      ));

    if (
      (promocode // promocode presence AND
        && (isPromocodeException // whether the property is an exception
          || ( // OR the promocode is blacklisted
            promocodeBlacklist.includes(promocode)
            || promocodeBlacklist.some(function (pattern) {
              return !!promocode.match(new RegExp(pattern));
            })
          )))
      || hardRockHotelSanDiegoPromoExclusion
    ) {
      this.logger.warn('Promocode found and handled: avoid PW comparison');
      // Do not execute PW at all
      return widgetData;
    }

    const rate = thn.parseUrlParameter('rate');
    const room = thn.parseUrlParameter('room');
    const roomcategory = thn.parseUrlParameter('roomcategory');
    const rateWhitelistUserIds = [
      1033910, // Brownsword Hotels
      1032762, // Playa resorts
      1036146, // Hard Rock Panama Megapolis
    ];

    if (
      [1033004].includes(thn.data.accountUserId)
      && (((thn.parseUrlParameter('configcode') === 'GPS20'
        || thn.parseUrlParameter('config')) === 'GPS20')
        || ((thn.parseUrlParameter('configcode') === 'GROUPS'
          || thn.parseUrlParameter('config')) === 'GROUPS')
        // Appears in the URL *config=GROUPS* instead of config=GROUPS
        || ((thn.parseUrlParameter('*config') === 'GPS20*'
          || thn.parseUrlParameter('*config')) === 'GROUPS*'))
    ) {
      return widgetData;
    }

    const roomWhitelistPerUser = {
      // Brownsword Hotels ("Confortable")
      1033910: ['CMD'],
      // Troutbeck
      1034333: ['1'],
    };

    if ([1034741, 1034262].includes(thn.data.accountUserId)) {
      // Oberoi hotels & Trident hotels client requested to block PW for all those rates
      const groupRate = thn.parseUrlParameter('rate');
      const oberoiRatesBlacklist = new RegExp([
        'WEBAXS-BAR15',
        'WEBAXS-BBR15',
        'WEBCOR-PTG',
        'WEBCOR-PTH',
        'WEBCOR-PTS',
        'WEBCOR-PUH',
        'WEBCOR-PUG',
        'WEBCOR-PWE',
        'CRMPRO-PUG01',
        'CRMPRO-PUH01',
        'WEBCOR-PHV',
        'WEBBMW-BAR15',
        'WEBBMW-BBR15',
        'WEBBAY-PUH',
        'WEBSBI-BAR20',
        'WEBSBI-BBR20'].join('|'));
      if (groupRate && groupRate.match(oberoiRatesBlacklist)) {

        return widgetData;
      }
    }

    // For p1027419, we need to use the flag priceSpecial
    // when we detect a value on room category an is not LODGE (is the Best Available Rate)
    if (
      thn.data.propertyId === 1027419 && roomcategory
      && roomcategory !== 'LODGE'
    ) {
      widgetData.priceSpecial = 1;
    }

    // "thn.dataLayerHelper.get()" doesn't always bring the last result.
    const roomFilters = !thn.empty(dataLayer) && Array.from(dataLayer)
      .filter((data) => data.event === 'rooms')
      // AvailabilityFilters only contains 'roomClassFilter: "all"' when using the default currency
      // If we change the currency, an object called 'price' will appear which contains the cheapest and the most expensive price rate
      // We need to check the length of RoomType to chewck if any room filter is applied
      // "RoomType" contains an array of all the filters applied
      .map((data) => data.AvailabilityFilters.RoomType).reverse()[0];

    // "AvailabilityFilters.RoomType" object contains a list of all the promocodes applied
    const activeRoomFilters = (!thn.empty(roomFilters) && roomFilters.length)
      // DOM alternative, check for filter "badges" under the filter box (doesn't detect price min-max slider)
      || ($ && $('.SearchFltrInfo.InfoMsg').length > 0)
      || document.querySelector('.filter-bar_tagGroup')
      // Mark price special when filtering by room type and the room type is tnot the basic one, ROOMS
      || document.querySelector(
        '[id$=roomCategory]:not(#ROOMS_roomCategory):checked',
      );

    // 2018-08-10: Do not execute PW if any rate is found in the URL parameters (with exceptions)
    // 2018-09-04: Do not execute PW if an specific room is shown (with exceptions)
    if (
      (!thn.empty(rate)
        && !rateWhitelistUserIds.includes(thn.data.accountUserId))
      || activeRoomFilters
      || (!thn.empty(room)
        && !roomWhitelistPerUser[thn.data.accountUserId]?.includes(room))
    ) {
      widgetData.priceSpecial = 1;
    }

    // Include language
    widgetData.language = thn.data.language;

    // Synxis Mobile/Desktop version
    widgetData.dateBegin = (_GlobalTokens && _GlobalTokens.ArrivalDtYYYYMMDD
      && _GlobalTokens.ArrivalDtYYYYMMDD.replace(/-/g, '/'))
      || (window.dataLayer && thn.arrayGet(dataLayer, 'SynxisArrivalSearch'))
        ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2')
      || (window.dataLayer && thn.arrayGet(dataLayer, 'arrivalDate'))?.replace(
        /(\d+)\D+(\d+)\D+(\d+)/,
        '$3/$1/$2',
      )
      || (window.dataLayer && thn.arrayGet(dataLayer, 'ArrivalDtYYYYMMDD'))
        ?.replace(/-/g, '/')
      || (window.dataLayer && thn.arrayGet(dataLayer, 'ArrivalDtMMDDYYYY'))
        ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2')
      || (window.dataLayer && thn.arrayGet(dataLayer, 'ArrivalDtDDMMYYYY'))
        ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');
    widgetData.dateEnd = (_GlobalTokens && _GlobalTokens.DepartDtYYYYMMDD
      && _GlobalTokens.DepartDtYYYYMMDD.replace(/-/g, '/'))
      || (window.dataLayer && thn.arrayGet(dataLayer, 'SynxisDepartureSearch'))
        ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2')
      || (window.dataLayer && thn.arrayGet(dataLayer, 'departureDate'))?.replace(
        /(\d+)\D+(\d+)\D+(\d+)/,
        '$3/$1/$2',
      )
      || (window.dataLayer && thn.arrayGet(dataLayer, 'DepartDtYYYYMMDD'))
        ?.replace(/-/g, '/')
      || (window.dataLayer && thn.arrayGet(dataLayer, 'DepartDtMMDDYYYY'))
        ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$1/$2')
      || (window.dataLayer && thn.arrayGet(dataLayer, 'DepartDtDDMMYYYY'))
        ?.replace(/(\d+)\D+(\d+)\D+(\d+)/, '$3/$2/$1');

    // In PID 1025754 we should use the URL params to obtain the dates (more reliable)
    if (thn.data.propertyId === 1025754) {
      const dateBeginUrl = thn.parseUrlParameter('arrive');
      const dateEndUrl = thn.parseUrlParameter('depart');

      if (dateBeginUrl && dateEndUrl) {
        widgetData.dateBegin = dateBeginUrl.replace(/-/g, '/');
        widgetData.dateEnd = dateEndUrl.replace(/-/g, '/');
      }
    }

    // piD 1016153 gets the date in different order, this may affect other properties
    if (
      (widgetData.dateBegin && widgetData.dateBegin.length
        && widgetData.dateBegin.split('/')
        && widgetData.dateBegin.split('/').length
        && widgetData.dateBegin.split('/')[1]
        && widgetData.dateBegin.split('/')[1].length > 2)
    ) {
      widgetData.dateBegin = widgetData.dateBegin.replace(
        /(\d+)\D+(\d+)\D+(\d+)/,
        '$2/$3/$1',
      );
    }
    if (
      widgetData.dateEnd && widgetData.dateEnd.length
      && widgetData.dateEnd.split('/')
      && widgetData.dateEnd.split('/').length
      && widgetData.dateEnd.split('/')[1]
      && widgetData.dateEnd.split('/')[1].length > 2
    ) {
      widgetData.dateEnd = widgetData.dateEnd.replace(
        /(\d+)\D+(\d+)\D+(\d+)/,
        '$2/$3/$1',
      );
    }

    const adults = thn.parseUrlParameter('adult');
    const children = thn.parseUrlParameter('child');
    // Parsing url if needed
    const parsedGuests = adults?.split(',')?.length > 1 ? [adults?.split(','), children?.split(',')] : null;
    /**
     * On multiple rooms cases, we usually only see one search (room),
     * then when we select the room, we are redirected to the following room to select
     * We should parse the data has if we were on a single room case,
     * except, that we need to make sure we obtain the occupacny of the active tab
     * This element only exist once we select a room (Summary)
     */
    const activeMultiroomOccupancyEl = document.querySelector(
      '.reservation-cart-container_multiReservation.reservation-cart-container_active .reservation-cart-container_guests',
    );
    if (activeMultiroomOccupancyEl || parsedGuests) {
      // Sometimes is Multiroom case, sometimes the param is just comma separated values
      if (!activeMultiroomOccupancyEl) {
        widgetData.adults = thn.parseNumber(parsedGuests?.[0]?.[0]);
        widgetData.children = thn.parseNumber(parsedGuests?.[1]?.[0]);
      } else {
        // Multiroom case - get the active tab occupancy
        widgetData.adults = thn.parseNumber(
          activeMultiroomOccupancyEl.querySelector('span:nth-child(1)')
            ?.textContent || 2,
        );
        widgetData.children = thn.parseNumber(
          activeMultiroomOccupancyEl.querySelector('span:nth-child(2)')
            ?.textContent || 0,
        );
      }
    } else if (_GlobalTokens && !thn.empty(_GlobalTokens.AdultQty)) {
      widgetData.adults = (_GlobalTokens && _GlobalTokens.AdultQty
        && parseInt(_GlobalTokens.AdultQty, 10)) || 1;
      widgetData.children = (_GlobalTokens && _GlobalTokens.ChildQty
        && parseInt(_GlobalTokens.ChildQty, 10)) || 0;
    } else if (adults.length && children.length) {
      widgetData.adults = thn.parseNumber(adults) || 2;
      widgetData.children = thn.parseNumber(children) || 0;
    } else {
      const guestsEl = document.querySelector
        && document.querySelector('.reservation-cart-container_guests');
      if (guestsEl) {
        const guests = recognizeNaturalNumbers(guestsEl.textContent);
        widgetData.adults = guests[0] || 1;
        widgetData.children = guests[1] || 0;
      } else if ($) {
        widgetData.adults = parseInt($('.AdultQtyDropDown:eq(0)').val(), 10)
          || 1;
        widgetData.children = parseInt($('.ChildrenQtyDropDown:eq(0)').val(), 10) || 0;
      }
    }

    if (widgetData.adults) {
      widgetData.guests = Math.max(1, widgetData.adults + widgetData.children);
    }

    const $childages = $ && $('select[onchange^="UpdateChildAges"]');
    if (widgetData.children && $childages && $childages.length) {
      // Build the detailed room information
      const room1Info = {
        adults: widgetData.adults,
        children: widgetData.children,
        childrenAges: [],
      };
      for (let i = 0; i < widgetData.children; i += 1) {
        room1Info.childrenAges.push($childages.eq(i).val());
      }
      // add the room info to the "roomsDispo"
      widgetData.roomsDispo = [room1Info];
    }

    // Prices from datalayer (react version)
    const dlPrices = [];

    // widgetData.rooms = (window._GlobalTokens && _GlobalTokens.RmQty && parseInt(_GlobalTokens.RmQty, 10)) || 1;
    widgetData.rooms = 1; // as of 2017-06-07 we only request price for 1 room

    if ($ && window.location.hostname.match(/^mobile\./) && _GlobalTokens) {
      // ----------------------
      // Synxis Mobile version
      // ----------------------
      prices = [];
      perNight = $('.Price small').text().match(
        /per night|por noche|par nuit|pro nacht|por noite|每晚|박당|Ανά διανυκτέρευση|gecelik ücret|الرسوملكل ليلة/i,
      );
      nights = perNight
        ? 1
        : thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);

      widgetData.roomDetails = $(
        '#AvailResultsList > div, #AvailResults > ul li',
      ).map(function (idx, el) {
        var $el = $(el);
        var roomData = {
          id: '',
          name: $el.find('.ResultItemRateName, .ResultItemRate').text(),
          prices: $el.find('a[href*=id]').map(function (idx2, el2) {
            var $el2 = $(el2);
            var hrefMatch = $el2.attr('href').match(/id=([^&]*)/);
            var priceData = {
              id: hrefMatch.length > 1 ? hrefMatch[1] : '',
              name: $el2.find('.ResultItemName, .ResultItemRoom').text(),
              totalNight: thn.parseNumber($el2.find('.Price').text()) / nights,
            };
            // priceData.breakfast = !priceData.name.match(/\bonly\b/i);
            prices.push(priceData.totalNight);
            return priceData;
          }).toArray(),
        };
        if (roomData.name.match(/suite/i)) {
          roomData.type = 'suite';
        } else if (roomData.name.match(/superior/i)) {
          roomData.type = 'superior';
        } else {
          roomData.type = 'standard';
        }

        return roomData;
      }).toArray();
      if (prices.length) {
        widgetData.currency = $('.Price > strong + span:eq(0)').text();
        widgetData.price = Math.min(...prices);
        widgetData.set = true;
      } else {
        widgetData.noRooms = true;
      }

    } else {
      // -----------------------
      // Synxis Desktop version
      // -----------------------
      // Currency method 1
      let curr = Array.from(document.querySelectorAll('.CurrCode, .CurCode'))
        .filter(function (el) {
          return el.textContent.trim().length;
        });
      if (curr.length) {
        widgetData.currency = thn.getCurrencyCode(curr[0].textContent.trim());
      }
      // Currency method 2
      if (!widgetData.currency) {
        curr = document.querySelector
          && document.querySelector('input[id*=CurrencyCode]');
        if (curr && curr.value && synxisCurrencyCodes[curr.value]) {
          widgetData.currency = synxisCurrencyCodes[curr.value];
        }
      }
      // Currency method 3
      if (!widgetData.currency || widgetData.currency === 'HOTEL') {
        widgetData.currency = thn.parseUrlParameter('currency')
          || thn.parseUrlParameter('avcurrency') || window.hotelDefaultRatePrefix;
      }

      if (!engine._isReact()) {
        // ---------------------------------
        // Synxis Desktop version (classic)
        // ---------------------------------
        if (!$) { // Failsafe version when no jQuery
          // The system does not let have Bookings of more than 30 days.
          if (
            thn.empty(window.lowestAvePrice)
            && !!document.querySelector('.message_type-warning')
          ) {
            widgetData.noRooms = true;
          } else {
            price = thn.parseNumber(window.lowestAvePrice?.replace(/\s+/g, ''));
          }
        } else {
          const $freq = $('.PriceFreq:eq(0)');
          // text example "/ Night (2 Rooms)"
          // if nº of rooms from this text equals the nº of rooms searched
          // it means the final price includes all the rooms and must be set into widgetData

          const rooms = (_GlobalTokens && _GlobalTokens.RmQty
            && parseInt(_GlobalTokens.RmQty, 10)) || 1;

          if (rooms === thn.parseNumber($freq.text())) {
            priceWithRooms = true;
            widgetData.rooms = rooms;
          }

          perNight = true;
          const perStayRegexp = /stay|estad|aufenthalt|séjour|permanência|\d{1} Übernachtung\(en\)|\d{1} Noche\(s\)|\d{1} Nuit\(s\)|\d{1} Nott\(e\)i di soggiorno|\d{1} ночи\(ей\)/i;
          // Price per stay
          if ($freq.length && $freq.text().match(perStayRegexp)) {
            perNight = false;
          }

          nights = perNight
            ? 1
            : thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);

          prices = [];
          roomDetails = {};
          // In some properties, if we cannot find an specific rate, we should flag the search as special_rate
          const requiredRateList = 'LPFS';
          let isRequiredRatePresent = false;

          // Organized by Room
          $('.ProductsHeader[data-room-code]').each(function (idx, el) {
            var $el = $(el);
            var roomData = {
              id: $el.attr('data-room-code'),
              name: $el.find('.HeaderTitle').text().trim(),
              prices: [],
            };
            if (
              roomData.name.match(
                /\b(suite|presiden(c|t)ial|ejecutiva|executive)\b/i,
              )
            ) {
              roomData.type = 'suite';
            } else if (roomData.name.match(/\b(superior|deluxe)\b/i)) {
              roomData.type = 'superior';
            } else {
              roomData.type = 'standard';
            }
            if (thn.empty(roomDetails[roomData.id])) {
              roomDetails[roomData.id] = roomData;
            }
            let products = $el.next('.ProductsInCategory').get(0);
            if (!products) {
              products = thn.nearest($el.get(0), '.ProductsInCategory');
            }

            $(products).children('[data-rate-code]').each(function (idx2, el2) {
              var $el2 = $(el2);
              var priceUnit = $el2.find('.ProductPrice, .Price').last().text()
                .trim();
              const priceData = {
                id: $el2.attr('data-rate-code'),
                rate: '',
                name: $el2.find('.ProductName').text().trim(),
                // pass currency as param and let `parseNumber` handle special currencies
                // ['KWD', 'OMR', 'BHD']
                totalNight: thn.parseNumber(priceUnit, {
                  currency: widgetData.currency,
                }) / nights,
              };
              // Check if required rate is present
              if (priceData.id.match(new RegExp(requiredRateList, 'i'))) {
                isRequiredRatePresent = true;
              }
              // Avoid special prices
              if (thn.data.propertyId === 1014432) { // Aston at the Maui Banyan
                if (
                  !$el2.text().match(
                    /holoholo kamaaina deal|free daily car rental|membership|senior savings|jump start|id required|corporate rate/i,
                  )
                ) {
                  prices.push(priceData.totalNight);
                }
              } else {
                prices.push(priceData.totalNight);
              }

              roomDetails[roomData.id].prices.push(priceData);
            });
          });

          // Organized by Rate
          $('.ProductsHeader[data-rate-code]').each(function (idx, el) {
            var $el = $(el);
            var basePriceData = {
              id: $el.attr('data-rate-code'),
              rate: '',
              name: $el.find('.HeaderTitle').text().trim(),
            };
            var products = $el.next('.ProductsInCategory').get(0);
            if (!products) {
              products = thn.nearest($el.get(0), '.ProductsInCategory');
            }
            $(products).find('[data-room-code]').each(function (idx2, el2) {
              var $el2 = $(el2);
              var roomData = {
                id: $el2.attr('data-room-code'),
                name: $el2.find('.ProductName').text().trim(),
                prices: [],
                adults: widgetData.adults,
                children: widgetData.children,
              };
              if (
                roomData.name.match(
                  /\b(suite|presiden(c|t)ial|ejecutiva|executive)\b/i,
                )
              ) {
                roomData.type = 'suite';
              } else if (roomData.name.match(/\b(superior|deluxe)\b/i)) {
                roomData.type = 'superior';
              } else {
                roomData.type = 'standard';
              }
              if (thn.empty(roomDetails[roomData.id])) {
                roomDetails[roomData.id] = roomData;
              }
              let priceUnit;
              if (priceWithRooms) {
                priceUnit = $el.find(
                  'span[id$=DefaultPriceDataContainerLabel]:eq(0)',
                ).text().trim();
              } else {
                // Pass currency as param and let `parseNumber` handle special currencies
                // ['KWD', 'OMR', 'BHD']
                priceUnit = $el2.find('.ProductPrice, .Price').last().text()
                  .trim();
              }
              const priceData = {
                ...basePriceData,
                totalNight: thn.parseNumber(priceUnit, {
                  currency: widgetData.currency,
                }) / nights,
              };

              // Check if required rate is present
              if (basePriceData.id.match(new RegExp(requiredRateList, 'i'))) {
                isRequiredRatePresent = true;
              }

              // Avoid special prices when element text matches the following keywords
              // Don't add the 'i' param (don't make it case-insensitive). We had some problems with that, because it rates that we don't want.
              let synxisClassicRateBlacklist = /identifica|valid id|Senior|I Prefer|Government|CAA|AAA|ARP|AARP|holoholo kamaaina deal|free daily car rental|membership|senior savings|jump start|id required|corporate rate|SS|SRS|SSR|Senior|Military|MIL|MILT|HERO|SAVY|GM|SG |GOVN|GOVF|GOVS|FGOV|SGOV|Government|LEMART|OH10/;
              if (thn.data.accountUserId === 1033433) {
                synxisClassicRateBlacklist = /MIL|DISA|VADVR|CAA|AAA|ARP|SRS|SSR|SENIOR|GOV|GOVS|CYBER/;
              }

              if (!$el.text().match(synxisClassicRateBlacklist)) {
                prices.push(priceData.totalNight);
              }
              roomDetails[roomData.id].prices.push(priceData);
            });
          });

          if (thn.empty(prices) && !isSpinnerVisible) {
            widgetData.noRooms = true;
          } else {
            if (thn.data.propertyId === 1024425 && !isRequiredRatePresent) {
              widgetData.priceSpecial = 1;
            }
            widgetData.price = Math.min(...prices);
            widgetData.set = true;

            // Convert roomDetails to array
            if (Object.keys(roomDetails).length) {
              widgetData.roomDetails = [];
              Object.keys(roomDetails).forEach(function (idx) {
                widgetData.roomDetails.push(roomDetails[idx]);
              });
            }
          }
        }

      } else { // be.synxis.com
        // ---------------------------------
        // Synxis Desktop version (React)
        // ---------------------------------
        roomDetails = {};
        prices = [];
        const defaultRatesBlacklistPattern = engine._getRatesBlacklistRegexp();
        // Custom blacklist pattern per property
        // We define our regex on `property.settings` using the setting 'engine_rates_blacklist_pattern'
        const customRatesBlacklistPattern = thn.getSetting(
          'engine_rates_blacklist_pattern',
        );
        const ratesBlacklistRe = new RegExp(
          customRatesBlacklistPattern || defaultRatesBlacklistPattern,
          'i',
        );
        // Under 'datalayer rooms lowest price' comment, we use 'rates_blacklist_re' to filter by rate code.It's not advisable to use this variable to check the name of each rate, or we could stop having the expected behaviour.
        const getPrices = function () {
          let priceSelector = '.thumb-cards_cardItem .thumb-cards_price, .thumb-cards_priceValue, .thumb-cards_price';
          // We need to exclude all rates exept 'Best Flexible Rate' from Lodge from the Flying Horse (https://lodgeatflyinghorse.com/)
          if (thn.data.accountUserId === 1034100) {
            priceSelector = '.app_row > div:nth-child(1) .thumb-cards_cardItem .thumb-cards_price';
          }
          if (thn.data.propertyId === 1029991) {
            priceSelector = 'div.thumb-cards_price';
          }

          let domCurrency;
          let pricesEl = Array.from(document.querySelectorAll(priceSelector));
          // Filter prices by setting 'engine_rooms_filter'
          if (filterForRooms) {
            pricesEl = pricesEl.filter((el) => !el?.closest('.thumb-cards_cardItem')?.querySelector('h3 a')?.textContent?.match(filterForRooms));
          }
          prices = pricesEl.reduce(
            function (result, priceEl) {
              let priceElPrice;
              if (!domCurrency) {
                // DKK and SEK can have the same currency symbol ('kr'). To be sure which one it is, check the language of the page.
                const isDanishCurrency = thn.data.language === 'da'
                  && priceEl.textContent?.includes('kr.');
                const currentCurrency = isDanishCurrency
                  ? 'DKK'
                  : (thn.parseCurrency(priceEl.textContent)
                    || thn.parseUrlParameter('currency'));
                domCurrency = (/USD/).test(currentCurrency)
                  ? widgetData.currency
                  : currentCurrency;
                widgetData.currency = domCurrency;
              }
              // IDR prices format has been changed for all properties: '2,000,000' = 2.000.000
              if (['IDR', 'KWD', 'BHD'].includes(widgetData.currency)) {
                priceElPrice = thn.parseNumber(priceEl.textContent, {
                  allowThreeDecimals: false,
                });
              } else { priceElPrice = thn.parseNumber(priceEl.textContent); }

              const selector = priceEl.closest('.thumb-cards_byRate')
                ? '.thumb-cards_byRate'
                : '.thumb-cards_rate';

              const rateCodeFromSelector = priceEl.closest(selector)
                && priceEl.closest(selector).getAttribute('data-rate-code');
              // When the direct parent has no data-rate-code, we need to get it from another ancestor
              const rateCodeFromId = document.querySelector('#auto-card-0')
                && document.querySelector('#auto-card-0').getAttribute(
                  'data-rate-code',
                );

              const rateCode = rateCodeFromSelector || rateCodeFromId || '';

              const rateCodeExclude = !rateCode.match(ratesBlacklistRe)?.filter(
                Boolean,
              )?.length;

              // Retun no price if a rate code is found in the blacklist for the property
              if (rateCodeExclude && thn.data.propertyId === 1042713) {
                return;
              }
              // Avoid special prices when element text matches the following keywords
              if (wantsToUseAllRates || rateCodeExclude) {
                result.push(priceElPrice);
              }

              return result;
            },
            [],
          );

          /**
           * ***** Datalayer room prices *****
           * Search for prices in datalayer when the "view more prices" dropdown is active
           * These prices are always stored in the hotel's currency, and we need to convert them
           * to our desired currency.
           *
           *
           * Some hotels should not get the searched data from the dataLayer. When you get the data retrieved from the dataLayer, the PW is not refreshed when the promocode is applied.
           * When you come to R & R page from an offer page or a page that leads you to the search result of a specific room,
           * because of the data in the dataLayer, prices that are not visible on the page are compared and the cheapest price among the prices appears in the PW.
           */
          // Preferred Hotels, Oberoi Hotels
          const usersToExcludeDataLayerPrices = [
            1037718,
            1034262,
            1035919,
            1035594,
            1042737,
            1044066,
            1033061,
            1037566,
            1048220,
            1040848,
            1040508,
            1042465,
          ];
          const isUserToExcludeDataLayerPrices = usersToExcludeDataLayerPrices
            .includes(thn.data.accountUserId);

          if (
            ((document.querySelector('.thumb-cards_viewMore')
              || document.querySelector('.thumb-cards_roomTypeOptions')
              || document.querySelector('.thumb-cards_priceContainer'))
              && !isUserToExcludeDataLayerPrices) || isMaiamiCoconutGrove
          ) {
            let dlRooms = thn.objectGet(window.dataLayer, 'AvailableRooms', {
              first: false,
            }) || {};
            // Filter rooms by setting 'engine_rooms_filter'
            if (filterForRooms) {
              dlRooms = dlRooms?.filter((dlroom) => !dlroom?.RateName?.match(filterForRooms));
            }
            // currency in which the room prices are shown in DL
            const dlCurrency = thn.objectGet(dataLayer, 'ConvertedCurrCode', { first: false })
              || {};

            if (dlRooms.length && dlCurrency) {
              const iwm = window && window.__INITIAL_STATE__
                && window.__INITIAL_STATE__.iwm;
              // Legacy object where exchange rates are stored
              const exchangeRatesLegacy = (
                ((iwm && iwm.InteractionModel
                  && iwm.InteractionModel.ContentList) || [])
                  .filter((el) => el.Key === 'CurrencyList')[0] || {}
              ).Values;
              // New version of the exchange rates object (default)
              const exchangeRates = (iwm && iwm.iwmConfigData
                && iwm.iwmConfigData.iwmCurrencyExchange) || [];

              if (exchangeRates || exchangeRatesLegacy) {
                // User id the of SHL chain
                const shlUserId = 1041266;

                // Regular filter and map functions
                let filterFn = (i) => wantsToUseAllRates || !i.RateCode.match(ratesBlacklistRe);
                const mapFn = (i) => {
                  // There are properties that need to use the price with taxes, others without,
                  // and some others with only one of those options
                  // In Oberoi, we need to use the price without taxes (UID 1034262)
                  // In Preferred hotels as well (UID 1037718) Chatrium (UID 1033177)
                  let dlPrice = i.Price ? i.PriceWithTax : undefined;
                  if (
                    [
                      1034262,
                      1033177,
                      1036319,
                      1035723,
                      1035617,
                      1037718,
                      1036882,
                      1037917,
                      1041524,
                      1043156,
                      1043281,
                      1038510,
                      1040508,
                      1039498,
                      1162113,
                    ].includes(thn.data.accountUserId)
                    || isMaiamiCoconutGrove || isJungleBayDominica || !dlPrice
                  ) {
                    dlPrice = i.Price;
                  }

                  return dlPrice;
                };

                // Here we are filtering the datalayer prices with the room type shown for the SHL chain.
                if (thn.data.accountUserId === shlUserId) {
                  // We have the option to filter by Rooms or Rates for this we are using different selectors
                  const availableRoomsNameSel = (document.querySelectorAll(
                    '.thumb-cards_byRoom .app_heading1',
                  ).length
                    && document.querySelectorAll(
                      '.thumb-cards_byRoom .app_heading1',
                    ))
                    || (document.querySelectorAll(
                      '.thumb-cards_container .app_subheading2',
                    ).length
                      && document.querySelectorAll(
                        '.thumb-cards_container .app_subheading2',
                      ));
                  const availableRoomsName = [
                    ...new Set(
                      [...availableRoomsNameSel].map((x) => x.textContent),
                    ),
                  ];
                  const availableRoomsNameRegex = new RegExp(
                    availableRoomsName.join('|'),
                    'gi',
                  );
                  const availableRatesNameSel = (document.querySelectorAll(
                    '.thumb-cards_byRoom .app_subheading2',
                  ).length
                    && document.querySelectorAll(
                      '.thumb-cards_byRoom .app_subheading2',
                    ))
                    || (document.querySelectorAll(
                      '.thumb-cards_container .app_heading1',
                    ).length
                      && document.querySelectorAll(
                        '.thumb-cards_container .app_heading1',
                      ));
                  const availableRatesName = [
                    ...new Set(
                      [...availableRatesNameSel].map((x) => x.textContent),
                    ),
                  ];
                  const availableRatesNameRegex = new RegExp(
                    availableRatesName.join('|'),
                    'gi',
                  );
                  const regularFilterFn = filterFn;
                  filterFn = (i) => !regularFilterFn(i)
                    && i.RateName.match(availableRatesNameRegex)
                    && i.RoomName.match(availableRoomsNameRegex);

                  // Search filtred by room type
                  const dlRoomNames = [
                    ...new Set([...dlRooms.map((i) => i.RoomName)]),
                  ];
                  const searchFiltredByRoomType = availableRoomsName.length !== dlRoomNames.length;

                  // Search with offer applied
                  const offerCodeApplied = thn.parseUrlParameter('rate')
                    && thn.parseUrlParameter('rate') !== '';

                  if (
                    searchFiltredByRoomType || activeRoomFilters
                    || offerCodeApplied
                  ) {
                    widgetData.priceSpecial = 1;
                  }
                }

                const lowestPrice = Math.min(
                  ...dlRooms
                    .filter(filterFn)
                    .map(mapFn)
                    .filter(Boolean),
                );

                // Get currency exchange rates
                if (!widgetData.currency) {
                  widgetData.currency = dlCurrency;
                }
                const exchangeFrom = exchangeRates
                  ? exchangeRates[dlCurrency]
                  : exchangeRatesLegacy.filter((el) => el.Code === dlCurrency,
                  )[0]?.Value;
                const exchangeTo = exchangeRates
                  ? exchangeRates[widgetData.currency]
                  : exchangeRatesLegacy.filter((el) => el.Code === widgetData.currency,
                  )[0]?.Value;

                if (exchangeFrom && exchangeTo) {
                  // transform price back to its currency (in case the hotel has a default currency different than synxis)
                  const priceInBaseCurr = lowestPrice / exchangeFrom;
                  // transform again to the desired currency
                  const priceInDesiredCurr = priceInBaseCurr * exchangeTo;

                  if (priceInDesiredCurr) {
                    dlPrices.push(priceInDesiredCurr);
                  }
                }
              }
            }
          }

          /** 28/02/2019
           * pID 1016153 uses be.synxis.com but doesn't have impressions in the datalayer
           */
          impressions = thn.objectGet(dataLayer, 'impressions', { first: false }) || {};
          if (!thn.empty(impressions)) {
            impressions.forEach(function (item, idx) {
              if (!roomDetails[item.name]) {
                roomDetails[item.name] = {
                  name: item.name,
                  id: '',
                  prices: [],
                  adults: widgetData.adults,
                  children: widgetData.children,
                };
                if (item.name.match(/\bsuite\b/i)) {
                  roomDetails[item.name].type = 'suite';
                } else if (item.name.match(/\bsuperior\b/i)) {
                  roomDetails[item.name].type = 'superior';
                } else {
                  roomDetails[item.name].type = 'standard';
                }
              }
              roomDetails[item.name].prices.push({
                id: item.id,
                name: item.variant,
                totalNight: item.price,
                breakfast: !!item.variant.match(/\bbreakfast\b/i),
              });
            });
          }
        };
        if (!thn.synxisRoomUnavailable) {
          // 2018/07/30 - Prices don't change in the dataLayer object when the currency is changed.
          getPrices();
        } else {
          for (let i = window.dataLayer.length - 1; i > 0; i -= 1) {
            if (window.dataLayer[i].event === 'impressionGlobalReady') {
              impressions = window.dataLayer[i].ecommerce.impressions;

              break;
            }
          }

          getPrices();
        }
        if (Object.keys(roomDetails).length) {
          widgetData.roomDetails = [];
          Object.keys(roomDetails).forEach(function (idx) {
            widgetData.roomDetails.push(roomDetails[idx]);
          });
        }

        if (
          document.querySelector(
            '.product-availability-container_unavailableMessage',
          )
          || document.querySelector(
            '[class*=-availability-container_section] .message_type-warning [role="alert"]',
          )
        ) {
          widgetData.noRooms = true;
          return widgetData;
        }

        // In this case we grab always the last impressionLoad because the page is not
        // reloaded when the price changes, so the dataLayer is not reloaded from scratch
        // if the user changes the dates, to a more expensive date, we retrieve the price from the first search.
        const usersWithImpressionsLoad = [
          1048273, // Pinacle Hotels
        ];

        if (usersWithImpressionsLoad.includes(thn.data.accountUserId) && dataLayer.length > 1) {
          prices = [];
          let impressionsLoad = thn.dataLayerHelper.get('AvailableRooms');
          if (impressionsLoad) {
            impressionsLoad.forEach((item) => {
              prices.push(item?.Price);
            });
            impressionsLoad = null;
          }
        }

        price = [...prices].filter(Boolean).sort((a, b) => a - b)[0];
      }

      if (
        price && ((document.querySelector('.thumb-cards_primaryPriceText')
          && document.querySelector('.thumb-cards_primaryPriceText')
            .firstElementChild
          && document.querySelector('.thumb-cards_primaryPriceText')
            .firstElementChild.classList.contains(
              'price-and-nights-text_totalNightsStay',
            )) || (document.querySelector('.thumb-cards_priceValue')?.parentElement?.textContent?.match(/Per Stay|por estancia/)))
      ) {
        price /= thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
      }
      // dl_prices are always per night, apply after min_price from the dom has been calculated
      if (!thn.empty(dlPrices)) {
        widgetData.price = [price, ...dlPrices].filter(Boolean).sort((a, b) => a - b,
        )[0];
        widgetData.set = true;
      } else if (price) {
        widgetData.price = price;
        widgetData.set = true;
      }
    }
    // Get the price from datalayer when the price changes to arabic numbers
    if (dataLayer && !widgetData.price && widgetData.language.match(/ar/i)) {
      const totalDlPrices = thn.objectGet(dataLayer, 'impressions');
      const pricesDL = ([...totalDlPrices].map((minPrice) => minPrice.price))
        .filter(Boolean);
      if (!thn.empty(pricesDL)) {
        widgetData.price = Math.min(...pricesDL);
      }
    }

    // Set price whether including rooms or not
    if (widgetData.set && !priceWithRooms) {
      widgetData.price /= widgetData.rooms;
    }
    widgetData.roomDetails = engine._roomDetailsParseData();
    if (
      thn.parseUrlParameter('filter') && thn.parseUrlParameter('filter') !== '' && thn.parseUrlParameter('filter') !== 'ALL'
    ) {
      widgetData.priceSpecial = 1;
    }

    if (thn.data.propertyId === 1038710) {
      widgetData.price = Math.round(widgetData.price);
    }

    // Price Type (Barcelo)
    if (engine._isBarcelo()) {
      const configCode = thn.parseUrlParameter('config')
        || thn.objectGet(dataLayer, 'ConfigCode', { first: false });
      // Whene the code is 'GROSS' it means that taxes are included.
      const isTaxesIncluded = configCode?.toLowerCase()?.includes('gross');
      widgetData.priceType = isTaxesIncluded ? 'full' : 'base';
    }

    const isUserLogged = !!document.querySelector('.myaccount-container_user div[class*=myaccount-container_]');

    if (isUserLogged) {
      widgetData.priceSpecial = 1;
    }

    if (usersUsingRoomsDispoInstantBooking.includes(thn.data.accountUserId)) {
      const agesFromParam = thn.parseUrlParameter('childages')?.split('|') || [];
      const childrenAges = agesFromParam.map((age) => {
        const parsedAge = thn.parseNumber(age);
        return parsedAge === 0 ? 1 : parsedAge;
      }).filter(Boolean);

      widgetData.roomsDispo = [
        {
          adults: widgetData.adults,
          children: widgetData.children,
          childrenAges: childrenAges,
        },
      ];
    }

    if (widgetData.price || widgetData.noRooms) {
      return widgetData;
    }

    return {};
  };

  engine._roomDetailsParseData = function roomDetailsParseData() {
    const roomDetailsArray = [];
    const roomSelector = document.querySelectorAll('.thumb-cards_byRoom');
    const refundableText = /(Free cancellation)/i;
    if (roomSelector.length) {
      Array.from(roomSelector).forEach(function (element, idx) {
        const roomName = element.querySelector('.app_heading1')?.textContent
          || '';
        const roomAdults = thn.parseNumber(
          element.querySelector(
            '.guests-and-roomsize_item, .guests-and-roomsize_guests',
          )?.textContent,
        );
        const roomChildren = 0;
        const priceInfo = element.querySelectorAll('.thumb-cards_rate');
        const roomId = element.dataset?.roomCode || '';
        const roomData = {
          id: roomId,
          name: roomName,
          adults: roomAdults,
          children: roomChildren,
          prices: [],
        };

        if (roomName.match(/Family/i)) {
          roomData.type = 'family';
        } else if (roomName.match(/Superior/i)) {
          roomData.type = 'superior';
        } else if (roomName.match(/Suite/i)) {
          roomData.type = 'suite';
        } else if (roomName.match(/Deluxe/i)) {
          roomData.type = 'deluxe';
        } else if (roomName.match(/Premium/i)) {
          roomData.type = 'premium';
        } else if (roomName.match(/Premier/i)) {
          roomData.type = 'premier';
        } else {
          roomData.type = 'standard';
        }

        if (priceInfo.length) {
          Array.from(priceInfo).forEach(function (priceUnit) {
            const priceItemName = priceUnit.querySelector('.app_subheading2')?.textContent || '';
            const pricesEl = [
              ...priceUnit.querySelectorAll('.thumb-cards_price'),
            ].filter((el) => el.textContent);
            const price = thn.parseNumber(
              [...pricesEl].map((el) => thn.parseNumber(el.textContent)),
            );
            const textInfo = document.querySelector(
              '.thumb-cards_rateShortDesc, .product-icons_check',
            )?.textContent || '';
            const priceItemId = priceUnit.dataset?.rateCode || '';
            const priceData = {
              id: priceItemId,
              name: priceItemName,
              totalNight: price,
              refundable: (priceItemName.match(refundableText)
                || textInfo.match(refundableText))
                ? 1
                : 0,
            };
            roomData.prices.push(priceData);
          });
          roomDetailsArray.push(roomData);
        }

        return roomData;
      });
    }

    return roomDetailsArray;
  };

  /**
   * Implements **bookingId**.
   *
   * @memberOf module:bs_6
   * @this thn.widgets.booking
   * @see {@link Booking#setData}
   *
   * @param {object} ctx - The context object.
   */
  engine.bookingParseData = function bookingParseData(ctx) {
    const widgetData = {};
    const dataLayer = window.dataLayer;
    const _GlobalTokens = window._GlobalTokens || window._globalTracking;
    const isThaiCalendar = thn.parseUrlParameter('locale') === 'th-TH'
      || thn.data.language === 'th';
    let multiRooms = false;
    if (IS_RESERV_HOLD) { // Do not pick up reservations on hold
      widgetData.set = false;

      return widgetData;
    }

    // Dates from param
    const dateBeginFromUrl = thn.parseUrlParameter('arrive')?.replace(/-/g, '/');
    const dateEndFromUrl = thn.parseUrlParameter('depart')?.replace(/-/g, '/');

    // This is a cancellation or a booking without bookingId for Chatrium hotels
    if (
      (Array.from(
        document.querySelectorAll('.booking-summary-heading_cancelNumber'),
      ).filter(thn.isVisible).length)
      || (thn.data.accountUserId === 1034201
        && !document.querySelectorAll('.confirmation-message_confirmationNumber')
          .length)
    ) {
      widgetData.set = false;
    }

    // ID
    const bookingIdEls = [
      ...document.querySelectorAll('.confirmation-message_confirmationNumber'),
    ];
    const bookingIdElsFromCards = [
      ...document.querySelectorAll('.booking-summary-reservations_confirmationNumber'),
    ];
    const fallbackBookingEls = [
      ...document.querySelectorAll('.booking-summary-heading_confirmNumber'),
    ];
    let bookingIds;

    // multiple bookingId
    if (bookingIdEls.length || fallbackBookingEls.length || bookingIdElsFromCards.length) {
      bookingIds = bookingIdEls.map((el) => el.textContent.trim());
      if (!bookingIds.length && bookingIdElsFromCards.length) {
        bookingIds = bookingIdElsFromCards.map((id) => id?.textContent?.split(':')[1]?.trim());
      }
      if (!bookingIds.length) {
        bookingIds = fallbackBookingEls.map(function (el2) {
          const bookingId = el2.textContent?.match(/#(\S+)/);

          return bookingId && bookingId[1];
        });
      }
      if (bookingIds.length > 1) {
        multiRooms = true;
        widgetData.bookingId = bookingIds;
      } else {
        widgetData.bookingId = bookingIds[0];
      }
    }

    // Common data
    // Dates
    const dateBegin = thn.dataLayerHelper?.get('ArrivalDtYYYYMMDD')
      || translateDate(
        document.querySelector(
          '.booking-summary-reservations_dates > div:nth-child(2)',
        )?.textContent || '',
        thn.data.language || 'en',
      );
    const dateEnd = thn.dataLayerHelper?.get('DepartDtYYYYMMDD')
      || translateDate(
        document.querySelector(
          '.booking-summary-reservations_dates > div:nth-child(3)',
        )?.textContent || '',
        thn.data.language || 'en',
      );

    if (dateBegin && dateEnd) {
      widgetData.dateBegin = dateBegin;
      widgetData.dateEnd = dateEnd;
    }

    // promocode
    const filteredItems = window.dataLayer.filter((item) => item[0] === 'event' && item[1] === 'purchase',
    );
    const usersAreUsingOraculoPromoCode = [1046617];
    const lastItem = filteredItems[filteredItems.length - 1];
    const excludePromoHardRockHotelNewYork = thn.data.propertyId === 1037259 // We shouldn't save the promocode if the booked rate is not a rate with the promocode applied.
      && ((!thn.empty(thn.dataLayerHelper.get('PromoCode')) && thn.dataLayerHelper.get('PromoCode') !== thn.dataLayerHelper.get('RateCode'))
        || (!thn.empty(thn.dataLayerHelper.get('rpPromoCode')) && thn.dataLayerHelper.get('rpPromoCode') !== thn.dataLayerHelper.get('rpRateCode')));

    if (
      (thn.data.accountUserId === 1033643
        && !thn.dataLayerHelper.get('transactionPromoCode')
        && !thn.data.propertyId === 1027586)
      || (thn.data.accountUserId === 1036882
        && thn.objectGet(lastItem, 'items', { first: false })?.[0]
          ?.item_variant !== 'Special Offer')
      || excludePromoHardRockHotelNewYork
    ) {
      widgetData.promocode = '';
    } else if (usersAreUsingOraculoPromoCode) { // We take RateCode as promocode for these users
      widgetData.promocode = (thn.dataLayerHelper.get('rpRateCode') || thn.dataLayerHelper.get('RateCode'))?.split('RO')?.[0] || '';
    } else {
      widgetData.promocode = thn.widgets.price.getPromocode().promocode
        || thn.dataLayerHelper?.get('rpPromoCode')
        || thn.dataLayerHelper?.get('PromoCode') || '';
    }

    if (!multiRooms) {
      // Single rooms case (Default)
      // Occupancy
      const adults = thn.parseNumber(
        document.querySelector(
          '.booking-summary-reservations_guests > div > span:nth-child(1)',
        )?.textContent,
      );
      const children = thn.parseNumber(
        document.querySelector(
          '.booking-summary-reservations_guests > div > span:nth-child(2)',
        )?.textContent,
      ) || 0;

      if (adults) {
        widgetData.adult = adults;
        widgetData.children = children;
        if (widgetData.adult) {
          widgetData.guests = widgetData.adult + (widgetData.children || 0);
        }
      }

      // Prices
      const priceEl = (document.querySelector('.confirmation-summary_amount')
        || document.querySelector('.confirmation-summary_total'))?.textContent;
      let currency = priceEl && thn.parseCurrency(priceEl);
      if (!currency) {
        const currencyRaw = priceEl?.match(/[^\d\s,.]*$/);
        currency = currencyRaw && thn.parseCurrency(currencyRaw[0]);
      }

      if (priceEl && currency) {
        // Include currency to handle specific currency decimals
        widgetData.price = thn.parseNumber(priceEl, { currency: currency });
        widgetData.currency = currency;
      }
    } else if (multiRooms) {
      // **********  multiple rooms setup start ************* //
      // return an array of widgetData objects for every room booked
      // this process will store all the rooms as a separate booking
      thn.logger.debug('Multi rooms detected');
      const widgetDataMulti = [
        ...document.querySelectorAll('.booking-summary-reservations_perRoom'),
      ].map((room, index) => {
        // Each room can have it's own date range
        let roomDateBegin;
        let roomDateEnd;
        if (isThaiCalendar) {
          roomDateBegin = dateBeginFromUrl;
          roomDateEnd = dateEndFromUrl;
        } else {
          const datesDom = Array.from(room.querySelectorAll('.booking-summary-reservations_dates span:not([class])'))
            .map((date) => translateDate(date?.textContent, thn.data.language))
            .filter((date) => date);
          roomDateBegin = datesDom?.[0]
          || translateDate(room.querySelector('.booking-summary-reservations_dates > div:nth-child(2)')?.textContent, thn.data.language || 'en')
          || widgetData.dateBegin;
          roomDateEnd = datesDom?.[1]
          || translateDate(room.querySelector('.booking-summary-reservations_dates > div:nth-child(3)')?.textContent, thn.data.language || 'en')
          || widgetData.dateEnd;
        }

        const guestsArrayElement = room.querySelector('.booking-summary-reservations_guests')?.textContent?.split(',');
        const roomAdults = thn.parseNumber(guestsArrayElement?.[0]);
        const roomChildren = thn.parseNumber(guestsArrayElement?.[1]);

        const roomPriceEl = room.querySelector('.booking-summary-reservations_amount')
          || room.querySelector('.booking-summary-reservations_roomTotal');
        const roomPrice = thn.parseNumber(roomPriceEl?.textContent); // required
        const roomCurrency = thn.parseCurrency(
          room.querySelector('.booking-summary-reservations_roomTotal')
            ?.textContent,
        ); // required

        const roomData = {
          dateBegin: roomDateBegin,
          dateEnd: roomDateEnd,
          bookingId: widgetData.bookingId[index],
          adults: roomAdults,
          children: roomChildren,
          guests: roomAdults + roomChildren,
          price: roomPrice,
          currency: roomCurrency,
        };

        if (widgetData.promocode) {
          roomData.promocode = widgetData.promocode;
        }

        return roomData;
      });

      // double check that the amount of booking id's are equal to the amount of parsed room prices
      if (
        !thn.empty(widgetDataMulti)
        && widgetDataMulti.length === widgetData.bookingId.length
      ) {
        thn.logger.debug('Multi rooms setup complete');
        return widgetDataMulti;
      }
    }

    // Use datalayer as fallback
    if (dataLayer) {
      thn.logger.debug('Using dataLayer as fallback');
      if (!widgetData.bookingId) {
        const bookingId = thn.dataLayerHelper.get('transactionId')?.match(
          /\w*$/,
        );
        widgetData.bookingId = thn.dataLayerHelper.get('rpConfirmNo')
          || thn.dataLayerHelper.get('rp_id')
          || thn.arrayGet(dataLayer, 'SynxisTransactionId')
          || (bookingId && bookingId[0]);
      }

      if (!widgetData.dateBegin || !widgetData.dateEnd) {
        widgetData.dateBegin = (thn.arrayGet(dataLayer, 'SynxisArrivalSearch')
          || thn.dataLayerHelper.get('bookingInformation.arrivalDate'))?.replace(
          /-/g,
          '/',
        )
          || (thn.dataLayerHelper.get('arrivalDate'))?.replace(
            /(\d+)\D+(\d+)\D+(\d+)/,
            '$3/$1/$2',
          );
        widgetData.dateEnd = (thn.arrayGet(dataLayer, 'SynxisDepartureSearch')
          || thn.dataLayerHelper.get('bookingInformation.departureDate'))
          ?.replace(/-/g, '/')
          || (thn.dataLayerHelper.get('departureDate'))?.replace(
            /(\d+)\D+(\d+)\D+(\d+)/,
            '$3/$1/$2',
          );

        // piD 1016153 gets the date in different order, this may affect other properties
        if (
          (widgetData.dateBegin
            && widgetData.dateBegin.split('/')[1]?.length > 2)
          || (widgetData.dateEnd && widgetData.dateEnd.split('/')[1]?.length > 2)
        ) {
          widgetData.dateBegin = widgetData.dateBegin.replace(
            /(\d+)\D+(\d+)\D+(\d+)/,
            '$2/$3/$1',
          );
          widgetData.dateEnd = widgetData.dateEnd.replace(
            /(\d+)\D+(\d+)\D+(\d+)/,
            '$2/$3/$1',
          );
        }
      }

      // If either currency or price is empty, assign both again, DL values may have different settings than the ones from the DOM
      if (
        (!widgetData.currency || !widgetData.price)
        && (thn.data.accountUserId !== 1033643 || thn.data.propertyId === 1027586)
      ) {
        widgetData.currency = thn.arrayGet(dataLayer, 'transactionCurrency')
          || thn.arrayGet(dataLayer, 'Currency')
          || thn.dataLayerHelper.get('ecommerce.currencyCode')
          || thn.objectGet(dataLayer, 'currency');
        widgetData.price = parseInt(
          thn.dataLayerHelper.get('transactionTotal')
          || thn.objectGet(dataLayer, 'rpTotalCostWithTax')
          || thn.objectGet(dataLayer, 'rpTotalCost')
          || thn.dataLayerHelper.get(
            'ecommerce.purchase.actionField.roomPurchaseTotalPlusTaxes',
          ),
          10,
        );
      }
    }

    // Use GlobalToekns as fallback
    if (_GlobalTokens) {
      thn.logger.debug('Using _GlobalTokens as fallback');
      if (!widgetData.bookingId) {
        widgetData.bookingId = _GlobalTokens.ConfirmNo;
      }
      if (!widgetData.dateBegin || !widgetData.dateEnd) {
        widgetData.dateBegin = _GlobalTokens.ArrivalDtYYYYMMDD?.replace(/-/g, '/');
        widgetData.dateEnd = _GlobalTokens.DepartDtYYYYMMDD?.replace(/-/g, '/');
      }

      if (
        (!widgetData.price || !widgetData.currency)
        && (_GlobalTokens.TotalCostWithTax && _GlobalTokens.CurrCode)
      ) {
        widgetData.price = _GlobalTokens.TotalCostWithTax;
        widgetData.currency = _GlobalTokens.CurrCode;
      }
    }

    // Fallback price
    if (!widgetData.price) {
      let price1 = document.getElementsByClassName('ResTotalValue'),
        price2 = document.getElementsByClassName('ResPriceDetailValue');
      if (price1.length) {
        price1 = price1[0].innerText || price1[0].textContent;
        if (!widgetData.price) {
          widgetData.price = thn.parseNumber(price1);
        }
        if (!widgetData.currency) {
          widgetData.currency = price1.substr(0, 3);
        }
      } else if (price2.length) {
        price2 = price2[price2.length - 1].textContent;
        if (!widgetData.price) {
          // Price element includes currency, it's needed to handle specific currencies with three decimals
          widgetData.price = thn.parseNumber(price2);
        }
        if (!widgetData.currency) {
          const currency = price2.match(/[^\s\d.,]+/);
          widgetData.currency = currency && currency[0];
        }
      }
    }

    // Use URL parameters as last fallback
    if (!widgetData.dateBegin || !widgetData.dateEnd || isThaiCalendar) {
      thn.logger.debug('Using URL parameters as last fallback for dates');
      widgetData.dateBegin = dateBeginFromUrl;
      widgetData.dateEnd = dateEndFromUrl;
    }

    if (!widgetData.adults) {
      thn.logger.debug('Using URL parameters as last fallback for guests');
      widgetData.adult = thn.parseNumber(thn.parseUrlParameter('adult'));
      widgetData.children = thn.parseNumber(thn.parseUrlParameter('child'));
      if (widgetData.adult) {
        widgetData.guests = widgetData.adult + (widgetData.children || 0);
      }
    }

    if (!widgetData.currency) {
      thn.logger.debug('Using URL parameters as last fallback for currency');
      widgetData.currency = thn.parseCurrency(
        thn.parseUrlParameter('currency'),
      );
    }

    return widgetData;
  };

  const findPromo = function (mapping, get) {
    let found;
    Object.keys(mapping).some(function (promocodeType) {
      return mapping[promocodeType].some(function (variable) {
        const promocode = get(variable);
        if (promocode) {
          found = {
            promocode,
            promocodeType,
          };

          return true;
        }

        return false;
      });
    });

    return found;
  };

  /**
   * Extracts promocode from the dataLayer.
   * Variables seem to be named as in classic gc.synxis.com
   */
  engine._getPromocodeFromDatalayer = function _getPromocodeFromDatalayer() {
    const dataLayer = window.dataLayer;
    if (!dataLayer) {
      return undefined;
    }

    const get = (key) => thn.dataLayerHelper.get(key);
    let mapping = {
      default: ['PromoCode'],
      group: ['GroupCode'],
      // 'corpo': ['CorpCode'],
    };
    // When two types of promocode are applied (group and promo), Verdi Hotels prioritizes the group code one
    if (thn.data.accountUserId === 1047208) {
      mapping = {
        group: ['GroupCode'],
        default: ['PromoCode'],
      };
    }

    return findPromo(mapping, get);
  };

  /**
   * Extracts promocode from qrs (represents the query string)
   * found in be.synxis.com
   */
  engine._getPromocodeFromQrs = function _getPromocodeFromQrs() {
    const qrs = window.qrs;
    if (!qrs) {
      return undefined;
    }

    const get = (key) => qrs[key];
    const mapping = {
      default: ['promo', 'coupon'],
      // 'corpo': ['corp'],
      group: ['group'],
      rate: ['rate'],
    };

    return !!thn.parseUrlParameter('promo') && findPromo(mapping, get);
  };

  /**
   * Extracts promocode from the classic _GlobalTokens
   * found in gc.synxis.com
   */
  engine._getPromocodeFromGlobals = function _getPromocodeFromGlobals() {
    const _GlobalTokens = window._GlobalTokens || window._globalTracking;
    if (!_GlobalTokens) {
      return undefined;
    }

    const get = (key) => _GlobalTokens[key];
    const mapping = {
      default: ['PromoCode'],
      group: ['GroupCode'],
      // 'corpo': ['CorpCode'],
      // 'rate': ??
    };

    return findPromo(mapping, get);
  };

  engine._getPromocodeFromReztrack = function _getPromocodeFromReztrack() {
    const reztrack = window._reztrack && window._reztrack.send;

    if (!reztrack) {
      return undefined;
    }

    const get = (key) => reztrack[key];
    const mapping = {
      default: ['promo'],
    };

    return findPromo(mapping, get);
  };

  /**
   * @memberOf module:bs_6
   * @see {@link Price#get_promocode}
   *
   * @param {object} ctx - The context object.
   */
  engine.getPromocode = function getPromocode(ctx) {

    // If any of this elements are visible in the DOM
    // then we consider the promocode as *not applied*
    const errorEl = document.querySelector('.NoAvailMsg, .WarningMsg');
    if (errorEl && thn.isVisible(errorEl)) {
      return {
        promocode: '', // Explicitly invalidate
      };
    }
    if ([1032506, 1039476, 1044655].includes(thn.data.accountUserId)) {
      return {
        promocode: thn.parseUrlParameter('promo'),
      };
    }
    if ([1034262, 1034741, 1046511].includes(thn.data.accountUserId)) {
      return {
        promocode: thn.parseUrlParameter('rate'),
      };
    }

    // All this functions return object OR undefined.
    // If the final result is undefined, then core continues
    // with whatever itself detected (available to us on "ctx")
    return engine._getPromocodeFromDatalayer() // 1st priority
      || engine._getPromocodeFromQrs() // 2nd
      || engine._getPromocodeFromGlobals() // 3rd
      || engine._getPromocodeFromReztrack(); // 4th
  };

  engine.onBeforeApplyDiscount = async (ctx) => {
    try {
      // https://be.synxis.com/?_ucs_=1&adult=2&arrive=2025-09-17&brand=OAKS&chain=18336&child=0&clearcache=all&config=OAKSGHA&configcode=OAKSGHA&currency=GBP&depart=2025-09-19&ga_client_id=621445723.1758099382&ga_session_id=1758120263&hotel=70515&level=hotel&locale=en-US&productcurrency=AUD&rooms=1&start=availresults&theme=OAKS&themecode=OAKS
      if (ctx.replayUrl) {
        const url = new URL(ctx.replayUrl);
        const currentCurrency = thn.parseCurrency(document.querySelector('.panel-currency span')?.textContent?.trim() || '');
        if (thn.data.pageName === 'Index' && thn.data.accountUserId === 1033061 && currentCurrency) {
          // send to BE with the same currency selected on Index
          url.searchParams.set('currency', currentCurrency);
        }
        ctx.replayUrl = url.toString();
      }
    } catch (_) { /* empty */ }
  };

  return engine;
}

export { factory };
